import ClinicalNoteProvenance from '@/features/clinical-actions/ClinicalNoteProvenance';
import usePatientPhotos from "@/features/clinical-actions/usePatientPhotos.js";
import ClinicalActionPanel from "@/features/clinical-actions/ClinicalActionPanel.jsx";
import { appointmentReservationCall } from '@/services/appointmentPaymentRouting.js';
// src/pages/doctors/appointments/details/DoctorAppointmentDetails.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, RefreshCw } from "lucide-react";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Divider from "@/components/ui/Divider.jsx";
import {
  BasicDialogFrame,
  BASIC_DIALOG_ACTION_CLASS,
} from "@/components/ui/BasicDialog.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import Select from "@/components/ui/Select.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { supabase } from "@/services/supabaseClient";
import { ehrStatusFromServer, submitConfirmedEhrRequest } from "@/services/confirmedEhrRequest.mjs";
import {
  loadAppointmentEhrAccessState,
  requestAppointmentEhrAccess,
  revokeAppointmentEhrAccess,
  resolvePatientAuthUserId,
} from "@/services/ehrAccessService.js";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import {
  buildInitialVitalRepStructuredClinicalData,
  buildVitalRepAuditTimeline,
  buildVitalRepSourceSnapshot,
  EMPTY_VITALREP_STRUCTURED_CLINICAL_DATA,
  hasVitalRepStructuredClinicalData,
  loadVitalRepNoteByAppointment,
  normalizeVitalRepStructuredClinicalData,
  VITALREP_VITALS_SOURCE,
} from "@/pages/doctors/appointments/lib/vitalRepNote.js";

import AppointmentCancelDialog from "@/pages/appointments/details/AppointmentCancelDialog.jsx";
import AppointmentDeleteDialog from "@/pages/appointments/details/AppointmentDeleteDialog.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import {
  APPOINTMENT_MISSED_GRACE_MS,
  getAppointmentLifecycleBucket,
} from "@/services/appointmentLifecycle.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { buildFavoriteDoctorIds } from "@/services/doctorFavorites.js";
import { buildAppointmentSmsConfirmation } from "@/services/accessLayerSmsService.js";

const VITALREP_AMENDMENT_DRAFT_PREFIX = "afrimed:vitalrep-amendment:";

function readVitalRepAmendmentDraft(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`) ||
      window.localStorage.getItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeVitalRepAmendmentDraft(appointmentId, draft) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return;
  try {
    const payload = JSON.stringify({
      ...(draft || {}),
      startedAt: draft?.startedAt || new Date().toISOString(),
    });
    window.sessionStorage.setItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`, payload);
    window.localStorage.setItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`, payload);
  } catch {
    // Best-effort continuity marker only.
  }
}
import {
  acquireSafeConsultationStream,
  stopConsultationStream,
} from "@/services/consultationDeviceSafety.js";
import videoConsultationService from "@/services/videoConsultationService.js";

/**
 * Doctor — Appointment Details (hydrated via route state + DB fallback)
 * Route: /doctors/appointments/:appointmentId
 *
 * ✅ Blueprint polish:
 * - Naked arrow_back (no button bg)
 * - Profile link + navigate_next icon, text-on-surface-var
 * - Confirmed + check icon
 * - Divider inset everywhere
 * - About section
 * - Notes card
 * - Patient Photos preview row + Show all
 * - Change / update ListItem (change_appointment + label + navigate_next)
 */

async function primeConsultationMediaPermission({ appointmentId = "" } = {}) {
  const stream = await acquireSafeConsultationStream({
    appointmentId,
    route: window.location?.pathname || "",
    preferVideo: true,
  });
  stopConsultationStream(stream);
}

// ✅ Real icon paths (your folder)
const ICONS = {
  back: "/icons/doctor/appointments/arrow_back.svg",
  next: "/icons/doctor/appointments/navigate_next.svg",

  chat: "/icons/doctor/appointments/chat.svg",
  messageBubble: "/icons/list-items/messages.svg",
  sms: "/icons/doctor/appointments/chat.svg",
  sendSms: "/icons/send.svg",
  call: "/icons/doctor/appointments/call.svg",

  schedule: "/icons/doctor/appointments/schedule.svg",
  first: "/icons/doctor/appointments/first_appointment.svg",
  evisit: "/icons/appointments/afrimed-EVisit.svg",
  meet: "/icons/afrimed-video.svg",
  hospital: "/icons/doctor/appointments/hospital_Fill.svg",

  pending: "/icons/doctor/appointments/Pending.svg",
  check: "/icons/doctor/appointments/check.svg",
  verified: "/icons/doctor/appointments/verified.svg",
  pendingVerification: "/icons/pending-verification.svg",
  ehr: "/icons/doctor/appointments/EHR.svg",
  ehrTonal: "/icons/doctor/appointments/EHR/EHR_Tonal.svg",
  ehrRequestPending: "/icons/list-items/history_toggle_off.svg",
  requestGranted: "/icons/doctor/appointments/EHR/request_granted.svg",
  blood: "/icons/doctor/appointments/bloodtype.svg",
  allergies: "/icons/doctor/appointments/allergies.svg",
  chronic: "/icons/doctor/appointments/chronic_conditions.svg",
  donor: "/icons/doctor/appointments/organ_donor.svg",
  consultType: "/icons/list-items/medical_services-filled.svg",
  diagnosis: "/icons/list-items/diagnosis_fill.svg",
  medication: "/icons/appointments/dr-e-prescription/medication.svg",
  biotech: "/icons/appointments/dr-e-prescription/biotech.svg",
  circleX: "/icons/pharmacy/circle-x.svg",
  file: "/icons/list-items/description_fill.svg",
  image: "/icons/list-items/imagesmode.svg",

  change: "/icons/doctor/appointments/change_appointment.svg",
  editSquare: "/icons/appointments/dr-e-prescription/edit.svg",
  scheduleFill: "/icons/list-items/schedule_Fill.svg",
  cancelAction: "/icons/list-items/cancel.svg",
  tonalEhr: "/icons/doctor/appointments/Request_EHR.svg",
  stethoscope: "/icons/appointments/stethoscope.svg",     // 18 x 18
  completeSmall: "/icons/doctor/check_small.svg",
};

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return languageCode || undefined;
}

const CUSTOM_LOCALE_LABELS = {
  sn: {
    monthsLong: [
      "Ndira",
      "Kukadzi",
      "Kurume",
      "Kubvumbi",
      "Chivabvu",
      "Chikumi",
      "Chikunguru",
      "Nyamavhuvhu",
      "Gunyana",
      "Gumiguru",
      "Mbudzi",
      "Zvita",
    ],
    weekdaysLong: ["Svondo", "Muvhuro", "Chipiri", "Chitatu", "China", "Chishanu", "Mugovera"],
    sex: {
      male: "Murume",
      female: "Mukadzi",
      other: "Zvimwe",
    },
  },
  zu: {
    monthsLong: [
      "Januwari",
      "Febhuwari",
      "Mashi",
      "Ephreli",
      "Meyi",
      "Juni",
      "Julayi",
      "Agasti",
      "Septhemba",
      "Okthoba",
      "Novemba",
      "Disemba",
    ],
    weekdaysLong: ["ISonto", "UMsombuluko", "ULwesibili", "ULwesithathu", "ULwesine", "ULwesihlanu", "UMgqibelo"],
    sex: {
      male: "Owesilisa",
      female: "Owesifazane",
      other: "Okunye",
    },
  },
};

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function restoreDraft(id, locationState) {
  // If navigation passed state, use it (this is the freshest truth)
  if (locationState && Object.keys(locationState || {}).length) {
    const nestedRestored =
      locationState.restored && typeof locationState.restored === "object"
        ? locationState.restored
        : null;

    if (nestedRestored) {
      return {
        ...nestedRestored,
        appointmentId: locationState.appointmentId || nestedRestored.appointmentId || nestedRestored.id || id,
        id: nestedRestored.id || locationState.appointmentId || id,
        patient: locationState.patient || nestedRestored.patient || null,
        patientId: locationState.patientId || nestedRestored.patientId || nestedRestored.patient_id || null,
        prescriptionId: locationState.prescriptionId || nestedRestored.prescriptionId || null,
      };
    }

    return locationState;
  }

  const key = `afrimed:doctor_appt:${String(id)}`;

  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}

  // ❗️IMPORTANT: no "last" fallback.
  // That was causing every appointment to show the most recently opened payload.

  return null;
}

function persistDraft(id, payload) {
  if (!payload) return;

  const key = `afrimed:doctor_appt:${String(id)}`;

  try {
    sessionStorage.setItem(key, JSON.stringify(payload));
    localStorage.setItem(key, JSON.stringify(payload));
    // ❗️Do NOT store a global "last" key — it corrupts other appointment restores.
  } catch {}
}

function normalizeStatus(raw) {
  const s = String(raw || "").toLowerCase();
  if (s.includes("pend")) return "pending";
  if (s.includes("confirm")) return "confirmed";
  if (s.includes("sched")) return "scheduled";
  if (s.includes("progress")) return "in_progress";
  if (s.includes("incomplete")) return "incomplete";
  if (s.includes("complete")) return "completed";
  if (s.includes("miss")) return "missed";
  if (s.includes("cancel")) return "cancelled";
  return raw ? s : "scheduled";
}

function normalizeVisitType(restored) {
  const raw =
    restored?.visitType ??
    restored?.mode ??
    restored?.appointmentType ??
    restored?.consultation_type ??
    (restored?.isEVisit ? "evisit" : null);

  const s = String(raw || "").toLowerCase();
  if (s === "evisit" || s === "virtual" || s === "private" || s === "telehealth") return "evisit";
  return "inperson";
}

function isMeaningfulText(value) {
  const text = String(value || "").trim();
  return Boolean(text && text !== "—" && text !== "-");
}

function firstMeaningfulText(values = [], fallback = "") {
  for (const value of values) {
    if (value === 0 || value === false) return String(value);
    if (isMeaningfulText(value)) return String(value).trim();
  }
  return fallback;
}

function canShowAccessLayerSmsPreview(search = "") {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(String(search || ""));
  if (params.get("debugAccessLayer") === "1" || params.get("debugSms") === "1") return true;
  const host = String(window.location?.host || "").toLowerCase();
  return (
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app")
  );
}

function shouldOpenAccessLayerSmsPreview(search = "") {
  const params = new URLSearchParams(String(search || ""));
  return params.get("debugAccessLayer") === "1" || params.get("debugSms") === "1";
}

function canShowAccessLayerSmsProofSend(search = "") {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(String(search || ""));
  if (params.get("debugSms") !== "1" && params.get("debugAccessLayer") !== "1") return false;
  const host = String(window.location?.host || "").toLowerCase();
  return (
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app")
  );
}

function normalizeSmsProofPhone(value) {
  return String(value || "").replace(/[\s().-]+/g, "").trim();
}

function formatAccessLayerDispatchTimestamp(value = "") {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return String(value || "");
  }
}

function formatAccessLayerProvider(value = "") {
  const provider = String(value || "").trim();
  if (provider.toLowerCase() === "twilio") return "Twilio";
  return provider || "Not available";
}

function normalizeClinicalDisplayText(value) {
  return String(value || "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/\(\s+/g, "(")
    .replace(/\s+\)/g, ")")
    .trim();
}

function cleanClinicalRawNoteForDisplay(value) {
  return String(value || "")
    .replace(/\\text\{([^}]*)\}/g, "$1")
    .replace(/\\\((.*?)\\\)/g, "$1")
    .replace(/\\ge/g, ">=")
    .replace(/\\le/g, "<=")
    .replace(/\\([{}()[\]<>%])/g, "$1")
    .replace(/\\\\/g, "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/([.!?])(?=\s*(?:Assessment|Plan|Diagnosis|Prescription|Treatment|Follow-up)\s*:)/gi, "$1 ")
    .replace(/[ \t]*•[ \t]*/g, "\n• ")
    .replace(/(?:^|\n)[ \t]*[-*][ \t]+/g, "\n• ")
    .replace(/(?:^|\n)[ \t]*(\d+)\.[ \t]+/g, "\n$1. ")
    .replace(/\n[ \t]*\n/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/^"+|"+$/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function parseClinicalDisplayLines(block = "") {
  const lines = String(block || "")
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const groups = [];

  lines.forEach((line) => {
    const bulletMatch = line.match(/^•\s+(.+)$/);
    const orderedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    const type = bulletMatch ? "ul" : orderedMatch ? "ol" : "p";
    const value = bulletMatch ? bulletMatch[1] : orderedMatch ? orderedMatch[2] : line;
    const last = groups[groups.length - 1];

    if ((type === "ul" || type === "ol") && last?.type === type) {
      last.items.push(value);
      return;
    }
    if (type === "p" && last?.type === "p") {
      last.items.push(value);
      return;
    }
    groups.push({ type, items: [value] });
  });

  return groups;
}

function isDuplicateClinicalText(value, existing = []) {
  const text = normalizeClinicalDisplayText(value).toLowerCase();
  if (!text) return false;
  return existing.some((item) => normalizeClinicalDisplayText(item).toLowerCase() === text);
}

function combineClinicalText(values = []) {
  const used = [];
  return values
    .map((value) => cleanClinicalRawNoteForDisplay(value))
    .filter(Boolean)
    .filter((value) => {
      if (isDuplicateClinicalText(value, used)) return false;
      used.push(value);
      return true;
    })
    .join("\n\n");
}

function sentenceCaseClinicalText(value) {
  const text = cleanClinicalRawNoteForDisplay(value);
  if (!text) return "";
  if (text.includes("\n")) return text;
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

function deriveTechnicalNoteFromRaw(rawNote = "") {
  const text = normalizeClinicalDisplayText(rawNote);
  const lower = text.toLowerCase();
  if (!text) return "";
  if (lower.includes("mute") || lower.includes("audio")) {
    return "Video consultation quality was difficult due to mute/audio issues.";
  }
  if (lower.includes("video") && (lower.includes("difficult") || lower.includes("quality"))) {
    return "Video consultation quality was difficult.";
  }
  if (lower.includes("connection") || lower.includes("network")) {
    return "Connection quality affected the consultation.";
  }
  return "";
}

function deriveAssessmentFromRaw(rawNote = "") {
  const lower = normalizeClinicalDisplayText(rawNote).toLowerCase();
  if (!lower) return "";
  if (lower.includes("patient is well") || lower.includes("patient was well")) {
    return "Patient generally stable.";
  }
  if (lower.includes("stable")) return "Patient generally stable.";
  return "";
}

function deriveTreatmentPlanFromRaw(rawNote = "") {
  const lower = normalizeClinicalDisplayText(rawNote).toLowerCase();
  if (!lower) return "";
  if (lower.includes("painkiller") || lower.includes("pain killer")) {
    return "Pain management advised.";
  }
  return "";
}

function deriveClinicalImpressionFromRaw(rawNote = "", diagnosisText = "") {
  const lower = normalizeClinicalDisplayText(rawNote).toLowerCase();
  if (!lower) return diagnosisText;
  if ((lower.includes("patient is well") || lower.includes("patient was well") || lower.includes("stable")) && !diagnosisText) {
    return "No acute concern documented.";
  }
  return diagnosisText;
}

function buildVitalRepClinicalDisplaySections({
  vitalRepNote,
  structuredData,
  sourceSnapshot,
  prescriptionMeta,
  prescriptionLabel,
  fulfillmentSummary,
  labels = {},
}) {
  const structured = structuredData && typeof structuredData === "object" ? structuredData : {};
  const snapshot = sourceSnapshot && typeof sourceSnapshot === "object" ? sourceSnapshot : {};
  const soapDraft = vitalRepNote?.soapDraft && typeof vitalRepNote.soapDraft === "object"
    ? vitalRepNote.soapDraft
    : null;
  if (soapDraft) {
    const notRecorded = labels.notRecorded || "Not recorded.";
    return [
      ["subjective", "Subjective", soapDraft.subjective],
      ["objective", "Objective", soapDraft.objective],
      ["assessment", "Assessment", soapDraft.assessment],
      ["plan", "Plan", soapDraft.plan],
      ["unsorted", "Unsorted clinical notes for review", soapDraft.unsorted],
    ]
      .filter(([, , value], index) => index < 4 || normalizeClinicalDisplayText(value))
      .map(([key, label, value]) => ({
        key,
        label,
        value: normalizeClinicalDisplayText(value) || notRecorded,
      }));
  }
  const hasSavedStructuredClinicalData = Boolean(
    structured.reasonForVisit ||
      structured.clinicalObservations ||
      structured.assessment ||
      structured.testsLabsRequested ||
      structured.medicationSummary ||
      structured.treatmentNextSteps ||
      structured.followUpAdvice
  );
  const rawNote = firstMeaningfulText([
    vitalRepNote?.finalNoteText,
    vitalRepNote?.generatedDraftText,
    snapshot.clinicianNotes,
    snapshot.diagnosisNotes,
  ]);
  const diagnosisTitle = firstMeaningfulText([
    structured.assessment,
    hasSavedStructuredClinicalData ? "" : snapshot.diagnosisTitle,
  ]);
  const visitReason = firstMeaningfulText([
    structured.reasonForVisit,
    hasSavedStructuredClinicalData ? "" : snapshot.serviceName,
    hasSavedStructuredClinicalData ? "" : snapshot.consultationType,
    hasSavedStructuredClinicalData ? "" : diagnosisTitle,
  ]);
  const objective = firstMeaningfulText([
    structured.testsLabsRequested,
    hasSavedStructuredClinicalData ? "" : deriveAssessmentFromRaw(rawNote),
  ]);
  const clinicalImpression = firstMeaningfulText([
    structured.assessment,
    hasSavedStructuredClinicalData ? "" : deriveClinicalImpressionFromRaw(rawNote, diagnosisTitle !== visitReason ? diagnosisTitle : ""),
  ]);
  const treatmentNextSteps = firstMeaningfulText([
    structured.treatmentNextSteps,
    structured.followUpAdvice,
    hasSavedStructuredClinicalData ? "" : deriveTreatmentPlanFromRaw(rawNote),
  ]);
  const medicationPlan = firstMeaningfulText([
    structured.medicationSummary,
    hasSavedStructuredClinicalData ? "" : prescriptionMeta?.medicineName,
    hasSavedStructuredClinicalData ? "" : prescriptionMeta?.medicine,
    hasSavedStructuredClinicalData ? "" : prescriptionMeta?.name,
    hasSavedStructuredClinicalData ? "" : prescriptionMeta?.title,
    hasSavedStructuredClinicalData ? "" : prescriptionLabel,
  ]);
  const treatmentPlan = combineClinicalText([treatmentNextSteps, medicationPlan]);
  const subjective = firstMeaningfulText([
    structured.clinicalObservations,
    visitReason,
  ]);
  const notRecorded = labels.notRecorded || "Not recorded.";
  const used = [];
  const sections = [];

  function pushSection(key, label, value, { optional = false, allowDuplicate = false } = {}) {
    const text = sentenceCaseClinicalText(value);
    if (!text && optional) return;
    const display = text || notRecorded;
    if (!allowDuplicate && display !== notRecorded && isDuplicateClinicalText(display, used)) {
      if (optional) return;
      sections.push({ key, label, value: notRecorded });
      return;
    }
    if (display !== notRecorded) used.push(display);
    sections.push({ key, label, value: display });
  }

  pushSection("visitReason", labels.visitReason || "Subjective", subjective);
  pushSection("objective", labels.objective || "Objective", objective, { optional: true });
  pushSection("clinicalImpression", labels.clinicalImpression || "Assessment", clinicalImpression);
  pushSection("treatmentPlan", labels.treatmentPlan || "Plan", treatmentPlan);

  return sections;
}

function buildVitalRepStructuredDataFromNote(note, sourceSnapshot = {}, prescriptionMeta = null) {
  const currentStructured = normalizeVitalRepStructuredClinicalData(note?.structuredClinicalData || {});
  if (hasVitalRepStructuredClinicalData(currentStructured)) return currentStructured;
  const soapDraft = note?.soapDraft && typeof note.soapDraft === "object" ? note.soapDraft : null;
  if (soapDraft) {
    return normalizeVitalRepStructuredClinicalData({
      reasonForVisit: soapDraft.subjective,
      clinicalObservations: soapDraft.objective,
      assessment: soapDraft.assessment,
      treatmentNextSteps: soapDraft.plan,
    });
  }
  return buildInitialVitalRepStructuredClinicalData(sourceSnapshot, prescriptionMeta);
}

function extractClinicalLabel(rawText = "", label = "") {
  const raw = normalizeClinicalDisplayText(rawText);
  const cleanLabel = String(label || "").trim();
  if (!raw || !cleanLabel) return "";
  const pattern = new RegExp(`${cleanLabel}\\s*:\\s*([\\s\\S]*?)(?=\\b(?:Assessment|Plan|Clinical impression|Diagnosis|Prescription|Treatment|Follow-up|Notes?)\\s*:|$)`, "i");
  const match = raw.match(pattern);
  return normalizeClinicalDisplayText(match?.[1] || "");
}

function buildDiagnosisClinicalDisplaySections({
  diagnosisTitle,
  diagnosisBody,
  appointmentReason,
  labels = {},
}) {
  const rawBody = normalizeClinicalDisplayText(diagnosisBody);
  const title = normalizeClinicalDisplayText(diagnosisTitle);
  const assessment = firstMeaningfulText([
    extractClinicalLabel(rawBody, "Assessment"),
    deriveAssessmentFromRaw(rawBody),
  ]);
  const clinicalImpression = [
    deriveClinicalImpressionFromRaw(rawBody, title),
    assessment,
  ]
    .map((item) => sentenceCaseClinicalText(item))
    .filter(Boolean)
    .filter((item, index, list) => !isDuplicateClinicalText(item, list.slice(0, index)))
    .join("\n");
  const notRecorded = labels.notRecorded || "Not recorded.";
  const sections = [{
    key: "clinicalImpression",
    label: labels.clinicalImpression || "Diagnosis and clinical impression",
    value: clinicalImpression || notRecorded,
  }];

  return sections.filter((section, index, list) => {
    if (section.value === notRecorded) return true;
    return !list
      .slice(0, index)
      .some((item) => item.value !== notRecorded && isDuplicateClinicalText(section.value, [item.value]));
  });
}

function logVideoConsultationStartDebug(payload = {}) {
  const host = String(window.location?.host || "").toLowerCase();
  const isDebugHost =
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host.includes("staging") ||
    host.includes("netlify.app");

  if (!isDebugHost) return;

  console.warn("[video-consultation-start]", {
    role: "doctor",
    route: window.location?.pathname || "",
    appointmentId: "",
    appointmentStatus: "",
    hasVideoSession: false,
    videoSessionStatus: "",
    action: "",
    errorName: "",
    errorMessage: "",
    ...payload,
  });
}

function ClinicalDisplayCard({ section }) {
  if (!section) return null;
  const value = cleanClinicalRawNoteForDisplay(section.value);
  const blocks = value.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);

  return (
    <Card className="px-4 py-3">
      <div className="text-label-medium text-on-surface-var">{section.label}</div>
      <div className="mt-1 space-y-2 text-body-medium leading-5 text-on-surface">
        {blocks.flatMap((block, blockIndex) =>
          parseClinicalDisplayLines(block).map((group, groupIndex) => {
            const key = `${blockIndex}-${groupIndex}`;
            if (group.type === "ul") {
              return (
                <ul key={key} className="list-disc space-y-1 pl-5">
                  {group.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              );
            }
            if (group.type === "ol") {
              return (
                <ol key={key} className="list-decimal space-y-1 pl-5">
                  {group.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ol>
              );
            }
            return (
              <p key={key} className="whitespace-pre-wrap">
                {group.items.join("\n")}
              </p>
            );
          })
        )}
      </div>
    </Card>
  );
}

function formatAuditTimestamp(value = "") {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return String(value || "");
  }
}

function VitalRepAuditTimeline({ items = [] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <Card className="px-4 py-3">
      <div className="text-label-medium text-on-surface-var">Audit timeline</div>
      <div className="mt-2 space-y-2">
        {items.map((item) => (
          <div key={item.id} className="text-body-medium leading-5 text-on-surface">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{item.label}</span>
              {item.timestamp ? (
                <span className="text-on-surface-var">{formatAuditTimestamp(item.timestamp)}</span>
              ) : null}
            </div>
            {item.reason ? (
              <div className="mt-0.5 text-body-small leading-5 text-on-surface-var">
                Reason: {item.reason}
              </div>
            ) : null}
            {item.clinician ? (
              <div className="mt-0.5 text-body-small leading-5 text-on-surface-var">
                Clinician: {item.clinician}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

function formatFulfillmentModeLabel(value = "", labels = {}) {
  return String(value || "").trim().toLowerCase() === "delivery"
    ? labels.delivery || "Delivery"
    : labels.pickup || "Pickup";
}

function buildDoctorFulfillmentSummary(meta = null, labels = {}) {
  const source = meta && typeof meta === "object" ? meta : {};
  const lastKind = String(source?.fulfillment_last_fola_kind || "").trim().toLowerCase();
  const paymentStatus = String(source?.payment_status || "").trim().toLowerCase();
  const pharmacyName = firstMeaningfulText(
    [
      source?.pharmacy_branch_name,
      source?.pharmacy_name,
      source?.requested_pharmacy_branch_name,
      source?.requested_pharmacy_name,
    ],
    "",
  );
  const modeLabel = formatFulfillmentModeLabel(
    source?.fulfillment_mode || source?.requested_fulfillment_mode || "pickup",
    {
      delivery: labels.deliveryMode || "Delivery",
      pickup: labels.pickupMode || "Pickup",
    },
  );
  const pharmacyFallback = labels.selectedPharmacy || "The selected pharmacy";

  if (lastKind === "pharmacy_ready_for_pickup") {
    return {
      label: labels.readyForPickup || "Ready for pickup",
      description: labels.readyForPickupDescription || `${pharmacyName || pharmacyFallback} marked this prescription as ready for pickup.`,
      helper:
        labels.fulfillmentModeComplete || `${modeLabel} handoff is complete on the pharmacy side.`,
      toneClass: "bg-[rgba(0,189,196,0.12)] text-[#008A8F]",
    };
  }

  if (paymentStatus === "paid") {
    return {
      label: labels.paid || "Paid",
      description:
        labels.paidDescription ||
        `${pharmacyName || pharmacyFallback} has a confirmed payment for this prescription.`,
      helper:
        labels.waitForPickup || "Wait for the pharmacy to mark the order ready for pickup.",
      toneClass: "bg-[rgba(0,189,196,0.12)] text-[#008A8F]",
    };
  }

  if (paymentStatus === "requires_payment") {
    return {
      label: labels.paymentReady || "Payment ready",
      description:
        labels.paymentReadyDescription ||
        `${pharmacyName || pharmacyFallback} set the quote and the patient can now pay in AfriMed.`,
      helper:
        labels.waitingForPayment || `${modeLabel} handoff is waiting for patient payment.`,
      toneClass: "bg-[rgba(24,50,119,0.08)] text-on-surface",
    };
  }

  if (lastKind === "pharmacy_preparing") {
    return {
      label: labels.pharmacyPreparing || "Pharmacy preparing",
      description:
        labels.pharmacyPreparingDescription ||
        `${pharmacyName || pharmacyFallback} accepted the prescription and is preparing it.`,
      helper:
        labels.patientHandledExternally ||
        `${modeLabel} coordination is handled by the patient, Fola, and pharmacy outside the clinician workflow.`,
      toneClass: "bg-[rgba(24,50,119,0.08)] text-on-surface",
    };
  }

  if (lastKind === "pharmacy_rejected") {
    return {
      label: labels.needsNewPharmacy || "Needs new pharmacy",
      description:
        labels.needsNewPharmacyDescription ||
        `${pharmacyName || pharmacyFallback} could not fulfil this prescription.`,
      helper:
        labels.chooseAnotherPharmacy ||
        "The patient needs to choose another signed-up pharmacy.",
      toneClass: "bg-[rgba(var(--clr-error),0.10)] text-[rgb(var(--clr-error))]",
    };
  }

  if (pharmacyName) {
    return {
      label: labels.pharmacySelected || "Pharmacy selected",
      description:
        labels.pharmacySelectedDescription ||
        `${pharmacyName} is the active ${modeLabel.toLowerCase()} handoff branch for this prescription.`,
      helper:
        labels.patientHandledExternally ||
        "Patient coordination is handled by the patient, Fola, and pharmacy outside the clinician workflow.",
      toneClass: "bg-[rgba(0,189,196,0.12)] text-[#008A8F]",
    };
  }

  return {
    label: labels.issued || "Issued",
    description:
      labels.issuedDescription ||
      "Prescription was issued. The patient starts pharmacy coordination after opening it in AfriMed™.",
    helper:
      labels.patientHandledExternally ||
      "Patient coordination is handled by the patient, Fola, and pharmacy outside the clinician workflow.",
    toneClass: "bg-[rgb(var(--clr-surface-container))] text-on-surface",
  };
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function appointmentDateToHHMM(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function appointmentDateToEndHHMM(value, durationMinutes = 0) {
  const date = value ? new Date(value) : null;
  const duration = Number(durationMinutes || 0);
  if (!date || Number.isNaN(date.getTime()) || !Number.isFinite(duration) || duration <= 0) {
    return "";
  }
  const end = new Date(date.getTime() + duration * 60 * 1000);
  return `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
}

function appointmentDateToISO(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function resolveDoctorIdentity(restored) {
  const doctor = restored?.doctor || restored?.doctorProfile || null;
  const name =
    doctor?.full_name ||
    doctor?.name ||
    doctor?.display_name ||
    restored?.doctor_name ||
    restored?.doctorName ||
    "Doctor";
  const photo =
    doctor?.photo_url ||
    doctor?.profile_image_url ||
    doctor?.avatar_url ||
    restored?.doctor_avatar ||
    restored?.doctorAvatar ||
    "";

  return { name, photo };
}

function calcAge(dobISO) {
  if (!dobISO) return null;
  const d = new Date(dobISO);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age <= 120 ? age : null;
}

function displayValue(
  value,
  fallback = "—",
  booleanLabels = { true: "Yes", false: "No" }
) {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) {
    const items = value.map((v) => String(v || "").trim()).filter(Boolean);
    return items.length ? items.join(", ") : fallback;
  }
  if (typeof value === "boolean") return value ? booleanLabels.true : booleanLabels.false;
  const text = String(value).trim();
  return text || fallback;
}

function mergeSnapshot(primarySnapshot, fallbackSnapshot) {
  if (!primarySnapshot) return fallbackSnapshot || null;
  if (!fallbackSnapshot) return primarySnapshot;

  const primaryAttachments = Array.isArray(primarySnapshot?.attachments)
    ? primarySnapshot.attachments
    : [];
  const fallbackAttachments = Array.isArray(fallbackSnapshot?.attachments)
    ? fallbackSnapshot.attachments
    : [];
  const primaryLabResults = Array.isArray(primarySnapshot?.labResults)
    ? primarySnapshot.labResults
    : [];
  const fallbackLabResults = Array.isArray(fallbackSnapshot?.labResults)
    ? fallbackSnapshot.labResults
    : [];
  const primaryTestResults = Array.isArray(primarySnapshot?.testResults)
    ? primarySnapshot.testResults
    : [];
  const fallbackTestResults = Array.isArray(fallbackSnapshot?.testResults)
    ? fallbackSnapshot.testResults
    : [];

  const fallbackPatient = fallbackSnapshot?.patient || fallbackSnapshot?.patientProfile || {};
  const primaryPatient = primarySnapshot?.patient || primarySnapshot?.patientProfile || {};

  return {
    ...fallbackSnapshot,
    ...primarySnapshot,
    patient: {
      ...fallbackPatient,
      ...primaryPatient,
    },
    patientProfile: {
      ...fallbackPatient,
      ...fallbackSnapshot?.patientProfile,
      ...primaryPatient,
      ...primarySnapshot?.patientProfile,
    },
    doctor: {
      ...(fallbackSnapshot?.doctor || {}),
      ...(primarySnapshot?.doctor || {}),
    },
    facility: {
      ...(fallbackSnapshot?.facility || {}),
      ...(primarySnapshot?.facility || {}),
    },
    when: {
      ...(fallbackSnapshot?.when || {}),
      ...(primarySnapshot?.when || {}),
    },
    ehrSummary: {
      ...(fallbackSnapshot?.ehrSummary || {}),
      ...(primarySnapshot?.ehrSummary || {}),
    },
    attachments: primaryAttachments.length > 0 ? primaryAttachments : fallbackAttachments,
    labResults: primaryLabResults.length > 0 ? primaryLabResults : fallbackLabResults,
    testResults: primaryTestResults.length > 0 ? primaryTestResults : fallbackTestResults,
    patientPhotos:
      Array.isArray(primarySnapshot?.patientPhotos) && primarySnapshot.patientPhotos.length > 0
        ? primarySnapshot.patientPhotos
        : fallbackSnapshot?.patientPhotos || [],
  };
}

function firstDefinedValue(values = [], fallback = "") {
  for (const value of values) {
    if (value === 0 || value === false) return value;
    if (value != null && String(value).trim() !== "") return value;
  }
  return fallback;
}

function resolvePatientUserId(...candidates) {
  return (
    firstDefinedValue(
      candidates.map((value) => {
        const text = String(value || "").trim();
        return text || null;
      }),
      null
    ) || null
  );
}

function formatDisplayTime(value, locale = "en-US") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const languageCode = String(locale || "").slice(0, 2);

  const dateFromRaw = (hours24, minutes) => {
    if (languageCode === "sn" || languageCode === "zu") {
      return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    const date = new Date(2026, 0, 1, hours24, minutes, 0, 0);
    try {
      return new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
    } catch {
      return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
  };

  const ampm = raw.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampm) {
    let hours = Number(ampm[1]);
    const minutes = Number(ampm[2]);
    const suffix = String(ampm[3]).toUpperCase();
    if (suffix === "AM") hours = hours === 12 ? 0 : hours;
    if (suffix === "PM") hours = hours === 12 ? 12 : hours + 12;
    return dateFromRaw(hours, minutes);
  }

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  const hours24 = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours24)) return raw;
  return dateFromRaw(hours24, minutes);
}

function normalizePriceLabel(value, fallbackCurrency = "ZAR") {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (raw === "—" || raw === "-") return "—";
  if (/^0(?:\.0+)?\s+[A-Z]{3}$/i.test(raw)) return "—";
  const currency = String(fallbackCurrency || "ZAR").trim() || "ZAR";
  const duplicateSuffix = new RegExp(`\\s+${currency}\\s+${currency}$`, "i");
  if (duplicateSuffix.test(raw)) {
    return raw.replace(duplicateSuffix, ` ${currency}`);
  }
  return raw;
}

function formatLongDateLabel(dateValue, locale = "en-US", fallback = "—") {
  const date = dateValue ? new Date(`${String(dateValue).slice(0, 10)}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return fallback;

  const languageCode = String(locale || "").slice(0, 2);
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    return `${custom.weekdaysLong[date.getDay()]}, ${custom.monthsLong[date.getMonth()]} ${date.getDate()}`;
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatCompactLongDate(dateValue, locale = "en-US", fallback = "—") {
  const date = dateValue ? new Date(`${String(dateValue).slice(0, 10)}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return fallback;

  const languageCode = String(locale || "").slice(0, 2);
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    return `${custom.weekdaysLong[date.getDay()]}, ${custom.monthsLong[date.getMonth()]} ${date.getDate()}`;
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
}

function getStartsInLabel(
  dateISO,
  startTime,
  nowMs = Date.now(),
  {
    defaultLabel = "Starts in 1 hour",
    startLabel = "Start consultation",
    minutesLabel = (count) => `Starts in ${count} min`,
    hoursLabel = (count) => `Starts in ${count} hour${count === 1 ? "" : "s"}`,
  } = {}
) {
  const datePart = String(dateISO || "").slice(0, 10);
  const timePart = String(startTime || "").trim();
  if (!datePart || !timePart) return defaultLabel;

  const normalizedTime = /[ap]m/i.test(timePart)
    ? (() => {
        const match = timePart.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
        if (!match) return "";
        let hours = Number(match[1]);
        const minutes = match[2];
        const suffix = String(match[3]).toUpperCase();
        if (suffix === "AM") hours = hours === 12 ? 0 : hours;
        if (suffix === "PM") hours = hours === 12 ? 12 : hours + 12;
        return `${String(hours).padStart(2, "0")}:${minutes}`;
      })()
    : timePart;

  if (!normalizedTime) return defaultLabel;

  const startAt = new Date(`${datePart}T${normalizedTime}:00`);
  if (Number.isNaN(startAt.getTime())) return defaultLabel;

  const diffMs = startAt.getTime() - Number(nowMs || Date.now());
  if (diffMs <= 5 * 60 * 1000) return startLabel;

  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 60) return minutesLabel(diffMinutes);
  const diffHours = Math.round(diffMinutes / 60);
  return hoursLabel(diffHours);
}

function getConsultationStartState(dateISO, startTime, nowMs = Date.now()) {
  const datePart = String(dateISO || "").slice(0, 10);
  const timePart = String(startTime || "").trim();
  if (!datePart || !timePart) return "countdown";

  const normalizedTime = /[ap]m/i.test(timePart)
    ? (() => {
        const match = timePart.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
        if (!match) return "";
        let hours = Number(match[1]);
        const minutes = match[2];
        const suffix = String(match[3]).toUpperCase();
        if (suffix === "AM") hours = hours === 12 ? 0 : hours;
        if (suffix === "PM") hours = hours === 12 ? 12 : hours + 12;
        return `${String(hours).padStart(2, "0")}:${minutes}`;
      })()
    : timePart;

  if (!normalizedTime) return "countdown";

  const startAt = new Date(`${datePart}T${normalizedTime}:00`);
  if (Number.isNaN(startAt.getTime())) return "countdown";

  const diffMs = startAt.getTime() - Number(nowMs || Date.now());
  if (diffMs > 5 * 60 * 1000) return "countdown";
  if (diffMs >= -APPOINTMENT_MISSED_GRACE_MS) return "start";
  return "closed";
}

function getStatusPresentation(status, labels = {}) {
  switch (status) {
    case "pending":
      return { label: labels.pending || "Pending", icon: ICONS.pending };
    case "scheduled":
      return { label: labels.scheduled || "Scheduled", icon: ICONS.pending };
    case "in_progress":
      return { label: labels.inProgress || "In progress", icon: ICONS.pending };
    case "incomplete":
      return { label: labels.incomplete || "Incomplete consultation", icon: ICONS.pending };
    case "completed":
      return { label: labels.completed || "Completed", icon: ICONS.check };
    case "missed":
      return { label: labels.missed || "Missed", icon: ICONS.pending };
    case "cancelled":
      return { label: labels.cancelled || "Cancelled", icon: ICONS.pending };
    case "confirmed":
    default:
      return { label: labels.confirmed || "Confirmed", icon: ICONS.check };
  }
}

function firstPositiveAmount(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return null;
}

function parseAmount(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function cleanPriceText(value) {
  const raw = String(value || "").trim();
  if (!raw || raw === "—" || raw === "-") return "";
  return raw;
}

function prettyPaymentMethod(value, fallback = "Debit / Credit cards") {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";

  const map = {
    credit: "Credit / Debit cards",
    debit: "Credit / Debit cards",
    card: "Credit / Debit cards",
    bank: "Bank transfers",
    transfer: "Bank transfers",
    eft: "Instant EFT",
    cash: "Cash on service",
    wallet: "E-wallets",
  };

  return map[raw] || fallback;
}

function TintedIcon({ src, className = "", color, opacity = 1 }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block shrink-0 ${className}`.trim()}
      style={{
        opacity,
        backgroundColor: color,
        maskImage: `url(${src})`,
        WebkitMaskImage: `url(${src})`,
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskPosition: "center",
        maskSize: "contain",
        WebkitMaskSize: "contain",
      }}
    />
  );
}

function ListRow({
  icon,
  label,
  value,
  right,
  onClick,
  clickable = false,
}) {
  const Wrapper = clickable ? "button" : "div";

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      className={[
        "w-full",
        "px-4 py-3",
        "flex text-left gap-3",
        clickable ? "active:bg-[rgba(2,6,23,0.03)]" : "",
      ].join(" ")}
    >
      <img src={icon} alt="" className="h-6 w-6 opacity-80 shrink-0" draggable="false" />

      <div className="min-w-0 flex-1">
        <div className="text-body-large text-on-surface truncate">{label}</div>
        {value ? (
          <div className="mt-0.5 text-body-medium text-on-surface-var truncate">{value}</div>
        ) : null}
      </div>

      {right ? <div className="shrink-0">{right}</div> : null}
    </Wrapper>
  );
}

function CompactAddRow({
  icon,
  label,
  actionLabel = "Add",
  onClick,
  iconStyle,
  as = "button",
  htmlFor,
}) {
  const Wrapper = as === "label" ? "label" : "button";
  return (
    <Wrapper
      type={Wrapper === "button" ? "button" : undefined}
      onClick={onClick}
      htmlFor={Wrapper === "label" ? htmlFor : undefined}
      className="flex min-h-[56px] w-full items-center gap-3 px-4 py-4 text-left"
    >
      <img
        src={icon}
        alt=""
        className="h-6 w-6 shrink-0"
        draggable="false"
        style={iconStyle}
      />
      <div className="min-w-0 flex-1 text-body-large text-on-surface">{label}</div>
      <div className="shrink-0 text-body-large text-[rgb(var(--afrimed-primary))]">{actionLabel}</div>
    </Wrapper>
  );
}

function CareActionRow({
  icon,
  title,
  body,
  actionLabel,
  onClick,
  disabled = false,
  iconStyle,
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left",
        disabled ? "cursor-not-allowed opacity-60" : "active:bg-[rgba(var(--clr-on-surface),0.03)]",
      ].join(" ")}
    >
      <img
        src={icon}
        alt=""
        className="h-6 w-6 shrink-0 opacity-80"
        draggable="false"
        style={iconStyle}
      />
      <div className="min-w-0 flex-1">
        <div className="text-body-large text-on-surface">{title}</div>
        <div className="mt-0.5 text-body-medium leading-5 text-on-surface-var">{body}</div>
      </div>
      {actionLabel ? (
        <div className="shrink-0 text-body-medium text-[rgb(var(--afrimed-primary))]">
          {actionLabel}
        </div>
      ) : null}
    </button>
  );
}

function buildLabItems(snapshot, photos) {
  const stateItems = [
    ...(Array.isArray(snapshot?.labResults) ? snapshot.labResults : []),
    ...(Array.isArray(snapshot?.testResults) ? snapshot.testResults : []),
    ...(Array.isArray(snapshot?.attachments) ? snapshot.attachments : []),
  ]
    .map((item, index) => {
      if (!item) return null;
      const title = displayValue(item.name || item.title || item.fileName, "");
      if (!title) return null;
      return {
        id: String(item.id || `state-item-${index}`),
        title,
        subtitle: displayValue(item.source || item.by || item.author, ""),
        icon: String(item.type || "").toLowerCase().includes("image") ? ICONS.image : ICONS.file,
        origin: item.origin || (Array.isArray(snapshot?.attachments) && snapshot.attachments.includes(item) ? "attachment" : "record"),
      };
    })
    .filter(Boolean);

  if (stateItems.length > 0) return stateItems.slice(0, 2);
  return [];
}

export default function DoctorAppointmentDetails() {
  const nav = useNavigate();
  const { appointmentId } = useParams();
  const location = useLocation();
  const { t, languageCode } = useLanguage();
  const { isDark } = useTheme();
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);
  const labAttachmentInputId = React.useMemo(
    () => `doctor-appointment-lab-upload-${String(appointmentId || "appointment")}`,
    [appointmentId],
  );
  const appointmentActionIconStyle = React.useMemo(
    () =>
      isDark
        ? {
            filter: "brightness(1.08) saturate(1.08) contrast(1.02)",
          }
        : undefined,
    [isDark],
  );
  const removeAttachmentIconColor = isDark
    ? "rgba(255,255,255,0.82)"
    : "rgb(var(--clr-on-surface-variant))";

  const dashLabel = "—";
  const patientFallbackLabel = t("doctors.appointmentDetails.patientFallback", "Patient");
  const doctorFallbackLabel = t("doctors.appointmentDetails.doctorFallback", "Doctor");
  const pageTitle = t("doctors.appointmentDetails.title", "Appointment details");
  const loadingBody = t(
    "doctors.appointmentDetails.loading",
    "Loading appointment details..."
  );
  const notFoundBody = t(
    "doctors.appointmentDetails.notFound",
    "Appointment not found. Please go back and open it again."
  );
  const backToAppointmentsLabel = t(
    "doctors.appointmentDetails.backToAppointments",
    "Back to appointments"
  );
  const firstAppointmentLabel = t(
    "doctors.appointmentDetails.firstAppointment",
    "First appointment"
  );
  const diagnosisTitleFallback = t(
    "doctors.appointmentDetails.diagnosisTitleFallback",
    "Diagnosis & Notes"
  );
  const meetPendingLabel = t(
    "doctors.appointmentDetails.meetPending",
    "Secure AfriMed™ consultation room"
  );
  const profileLabel = t("doctors.appointmentDetails.actions.profile", "Profile");
  const diagnosisLabel = t("doctors.appointmentDetails.sections.diagnosis", "Diagnosis");
  const editDiagnosisLabel = t(
    "doctors.appointmentDetails.actions.editDiagnosis",
    "Edit diagnosis"
  );
  const addLabel = t("doctors.appointmentDetails.actions.add", "Add");
  const viewLabel = t("doctors.appointmentDetails.actions.view", "View");
  const prescriptionSectionLabel = t(
    "doctors.appointmentDetails.sections.prescription",
    "Prescription"
  );
  const testsLabResultsLabel = t(
    "doctors.appointmentDetails.sections.testsAndLabs",
    "Tests & Lab results"
  );
  const testsDocumentsLabel = t(
    "doctors.appointmentDetails.sections.testsAndDocuments",
    "Tests & Documents"
  );
  const requestEhrLabel = t("doctors.appointmentDetails.requestEhr.title", "Request EHR");
  const requestEhrBodyLabel = t(
    "doctors.appointmentDetails.requestEhr.body",
    "Provide better care by accessing your patient Electronic Health Record"
  );
  const requestLabel = t("doctors.appointmentDetails.actions.request", "Request");
  const notesLabel = t("doctors.appointmentDetails.sections.additionalNotes", "Additional notes");
  const vitalRepSectionLabel = t(
    "doctors.appointmentDetails.vitalRep.section",
    "VitalRep™ consultation note"
  );
  const generateVitalRepLabel = t(
    "doctors.appointmentDetails.vitalRep.generate",
    "Open VitalRep™ beta"
  );
  const reviewVitalRepLabel = t(
    "doctors.appointmentDetails.vitalRep.reviewDraft",
    "Continue VitalRep™ draft"
  );
  const amendVitalRepLabel = t(
    "doctors.appointmentDetails.vitalRep.amend",
    "Amend note"
  );
  const vitalRepDraftReadyLabel = t(
    "doctors.appointmentDetails.vitalRep.draftReady",
    "VitalRep™ draft saved"
  );
  const vitalRepSavedLabel = t(
    "doctors.appointmentDetails.vitalRep.saved",
    "Consultation note saved"
  );
  const vitalRepLoadingLabel = t(
    "doctors.appointmentDetails.vitalRep.loading",
    "Loading VitalRep™ note"
  );
  const vitalRepHelperLabel = t(
    "doctors.appointmentDetails.vitalRep.helper",
    "Structured clinical documentation. Create, review, and finalise consultation notes."
  );
  const completionLockedLabel = t(
    "doctors.appointmentDetails.actions.consultationCompleted",
    "Consultation completed"
  );
  const completeConsultationRequirementsLabel = t(
    "doctors.appointmentDetails.completeConsultation.requirements",
    "Save the diagnosis and notes before completing the consultation."
  );
  const vitalRepPreviewFallbackLabel = t(
    "doctors.appointmentDetails.vitalRep.previewFallback",
    "Capture or paste consultation notes, then generate a SOAP Draft for doctor review before final sign-off."
  );
  const vitalRepEditorFinalHelperLabel = t(
    "doctors.appointmentDetails.vitalRep.editorFinalHelper",
    "This finalised consultation note is locked. Amendments require a reason and are saved with audit metadata."
  );
  const confirmedChipClass = "inline-flex min-w-0 max-w-full items-center rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small leading-[16px] text-[#008A8F] whitespace-nowrap";
  const neutralChipClass = "inline-flex min-w-0 max-w-full items-center rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small leading-[16px] text-on-surface whitespace-nowrap";
  const patientPhotosLabel = t(
    "doctors.appointmentDetails.sections.photos",
    "Patient Photos"
  );
  const showAllLabel = t("doctors.appointmentDetails.actions.showAll", "Show all");
  const photosEmptyLabel = t(
    "doctors.appointmentDetails.photosEmpty",
    "No patient photos shared yet."
  );
  const messageLabel = t("doctors.appointmentDetails.actions.message", "Message");
  const smsLabel = t("doctors.appointmentDetails.actions.sms", "SMS");
  const callLabel = t("doctors.appointmentDetails.actions.call", "Call");
  const eVisitLabel = "Virtual";
  const joinLabel = t("doctors.appointmentDetails.actions.join", "Join");
  const joinClosedLabel = t("doctors.appointmentDetails.actions.joinClosed", "Window closed");
  const updatingLabel = t("doctors.appointmentDetails.actions.updating", "Updating...");
  const ehrLabel = t("doctors.appointmentDetails.sections.ehr", "EHR");
  const detailsLabel = t("doctors.appointmentDetails.actions.details", "Details");
  const referralHandoverLabel = t(
    "doctors.appointmentDetails.referrals.handoverTitle",
    "Referral & handover"
  );
  const referralHandoverBodyLabel = t(
    "doctors.appointmentDetails.referrals.handoverBody",
    "Send this patient to specialist care or create a structured handover."
  );
  const openLabel = t("doctors.appointmentDetails.actions.open", "Open");
  const accessGrantedLabel = t(
    "doctors.appointmentDetails.ehr.accessGranted",
    "Access granted"
  );
  const accessRequestedLabel = t(
    "doctors.appointmentDetails.ehr.accessRequested",
    "Access requested"
  );
  const bloodTypeLabel = t("doctors.appointmentDetails.fields.bloodType", "Blood type");
  const allergiesLabel = t("doctors.appointmentDetails.fields.allergies", "Allergies");
  const chronicConditionsLabel = t(
    "doctors.appointmentDetails.fields.chronicConditions",
    "Chronic Conditions"
  );
  const organDonorLabel = t(
    "doctors.appointmentDetails.fields.organDonor",
    "Organ Donor"
  );
  const addNotesPlaceholder = t(
    "doctors.appointmentDetails.notesPlaceholder",
    "Add notes..."
  );
  const notesAutosaveLabel = t(
    "doctors.appointmentDetails.notesAutosave",
    "Notes auto-save when you leave this field."
  );
  const supportLabel = t(
    "doctors.appointmentDetails.sections.support",
    "Support and reschedule"
  );
  const changeAppointmentLabel = t(
    "doctors.appointmentDetails.actions.changeAppointment",
    "Change, reschedule or cancel appointment"
  );
  const cancelDialogTitle = t(
    "doctors.appointmentDetails.cancelDialog.title",
    "Cancel appointment?"
  );
  const cancelDialogDescription = t(
    "doctors.appointmentDetails.cancelDialog.description",
    "Please remember to reach out to the patient before proceeding."
  );
  const cancelConfirmLabel = t(
    "doctors.appointmentDetails.cancelDialog.confirm",
    "Yes, cancel"
  );
  const deleteDialogTitle = t(
    "doctors.appointmentDetails.deleteDialog.title",
    "Are you sure you want to delete this medical appointment?"
  );
  const deleteDialogDescription = t(
    "doctors.appointmentDetails.deleteDialog.description",
    "After deleting the medical appointment record, you will not be able to recover it."
  );
  const deleteConfirmLabel = t(
    "doctors.appointmentDetails.deleteDialog.confirm",
    "Delete"
  );
  const changeSheetTitle = t(
    "doctors.appointmentDetails.changeSheet.title",
    "Change • update appointment"
  );
  const requestNewTimeLabel = t(
    "doctors.appointmentDetails.changeSheet.requestNewTime",
    "Request new time"
  );
  const cancelAppointmentLabel = t(
    "doctors.appointmentDetails.changeSheet.cancelAppointment",
    "Cancel Appointment"
  );
  const startConsultationLabel = t(
    "doctors.appointmentDetails.actions.startConsultation",
    "Start consultation"
  );
  const markInProgressLabel = t(
    "doctors.appointmentDetails.actions.markInProgress",
    "Mark in progress"
  );
  const completeConsultationLabel = t(
    "doctors.appointmentDetails.actions.completeConsultation",
    "Complete consultation"
  );
  const unavailablePastAppointmentsLabel = t(
    "doctors.appointmentDetails.support.unavailablePastAppointments",
    "Unavailable for past appointments"
  );
  const startsInDefaultLabel = t(
    "doctors.appointmentDetails.startsIn.default",
    "Starts in 1 hour"
  );

  // strip one-time flags (keep state clean)
  const stateForRestore = React.useMemo(() => {
    const s = location.state || null;
    if (!s) return null;
    const {
      justConfirmed: _a,
      justRequestedReschedule: _b,
      justRescheduled: _c,
      justDeleted: _d,
      ...rest
    } = s;
    const stateAppt = String(rest?.appointmentId || rest?.id || "").trim();
    const urlAppt = String(appointmentId || "").trim();
    if (stateAppt && urlAppt && stateAppt !== urlAppt) return null;
    return rest;
  }, [appointmentId, location.state]);

  const restoredDraft = React.useMemo(
    () => restoreDraft(appointmentId, stateForRestore),
    [appointmentId, stateForRestore]
  );

  const [dbSnapshot, setDbSnapshot] = React.useState(null);
  const [snapshotLoading, setSnapshotLoading] = React.useState(true);
  const [authDoctorUserId, setAuthDoctorUserId] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDb() {
      try {
        setSnapshotLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const doctorId = authData?.user?.id || null;
        if (alive) {
          setAuthDoctorUserId(String(doctorId || "").trim());
        }
        const snapshot = await loadDoctorAppointmentSnapshot({
          appointmentId,
          doctorId,
        });
        if (!alive) return;
        setDbSnapshot(snapshot || null);
      } catch {
        if (!alive) return;
        setDbSnapshot(null);
      } finally {
        if (alive) setSnapshotLoading(false);
      }
    }

    hydrateFromDb();
    return () => {
      alive = false;
    };
  }, [appointmentId, restoredDraft]);

  const [liveSnapshot, setLiveSnapshot] = React.useState(null);
  const mergedSnapshot = React.useMemo(
    () => mergeSnapshot(dbSnapshot, restoredDraft),
    [dbSnapshot, restoredDraft]
  );
  const restored = liveSnapshot || mergedSnapshot;

  React.useEffect(() => {
    setLiveSnapshot(null);
  }, [appointmentId]);

  React.useEffect(() => {
    if (restored) persistDraft(appointmentId, restored);
  }, [appointmentId, restored]);

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [changeSheetOpen, setChangeSheetOpen] = React.useState(false);

  function handleBack() {
    if (vitalRepAmendmentDialogOpen) {
      setVitalRepAmendmentDialogOpen(false);
      setVitalRepError("");
      return;
    }
    if (changeSheetOpen) {
      setChangeSheetOpen(false);
      return;
    }
    const explicitReturnTo = String(location.state?.returnTo || "").trim();
    if (explicitReturnTo) {
      nav(explicitReturnTo, {
        replace: true,
        state: location.state?.returnState || location.state?.restored || null,
      });
      return;
    }
    if (window.history.length > 1) nav(-1);
    else nav("/doctors/appointments");
  }

  const {items:resolvedPatientPhotos}=usePatientPhotos(appointmentId);

const restoredWithPhotos = React.useMemo(
  () => ({
    ...(restored || {}),
    patientPhotos: resolvedPatientPhotos,
  }),
  [restored, resolvedPatientPhotos]
);

const initialNotes =
  restored?.notes ||
  restored?.patient_notes ||
  "";

const [notesDraft, setNotesDraft] = React.useState(initialNotes);
const [actionBusy, setActionBusy] = React.useState("");
const [actionError, setActionError] = React.useState("");
const [toastMsg, setToastMsg] = React.useState("");
const [accessLayerPreviewVersion, setAccessLayerPreviewVersion] = React.useState(0);
const [accessLayerSmsOpen, setAccessLayerSmsOpen] = React.useState(() =>
  shouldOpenAccessLayerSmsPreview(location.search)
);
const [accessLayerTestRecipientPhone, setAccessLayerTestRecipientPhone] = React.useState("");
const [accessLayerSmsSendBusy, setAccessLayerSmsSendBusy] = React.useState(false);
const [accessLayerSmsSendResult, setAccessLayerSmsSendResult] = React.useState(null);
const [accessLayerSmsRefreshBusy, setAccessLayerSmsRefreshBusy] = React.useState(false);
const [accessLayerSmsDispatchEvidence, setAccessLayerSmsDispatchEvidence] = React.useState(null);
const [accessLayerSmsDispatchEvidenceStatus, setAccessLayerSmsDispatchEvidenceStatus] =
  React.useState("idle");
const [completeDialogOpen, setCompleteDialogOpen] = React.useState(false);
  const [vitalRepNote, setVitalRepNote] = React.useState(null);
  const [vitalRepDraftText, setVitalRepDraftText] = React.useState("");
  const [vitalRepStructuredData, setVitalRepStructuredData] = React.useState(
    () => normalizeVitalRepStructuredClinicalData(EMPTY_VITALREP_STRUCTURED_CLINICAL_DATA)
  );
  const [vitalRepHydrated, setVitalRepHydrated] = React.useState(false);
  const [vitalRepAmendmentDialogOpen, setVitalRepAmendmentDialogOpen] = React.useState(false);
  const [vitalRepAmendmentReason, setVitalRepAmendmentReason] = React.useState("");
  const [vitalRepPreviewOpen, setVitalRepPreviewOpen] = React.useState(false);
  const vitalRepPreviewDetailsRef = React.useRef(null);
  const [vitalRepAmendmentDraft, setVitalRepAmendmentDraft] = React.useState(() =>
    readVitalRepAmendmentDraft(appointmentId)
  );
const [vitalRepBusy, setVitalRepBusy] = React.useState("");
const [vitalRepError, setVitalRepError] = React.useState("");
const attachmentInputRef = React.useRef(null);

  React.useEffect(() => {
    setNotesDraft(initialNotes);
  }, [appointmentId, initialNotes]);

React.useEffect(() => {
  if (shouldOpenAccessLayerSmsPreview(location.search)) {
    setAccessLayerSmsOpen(true);
  }
}, [location.search]);

  React.useEffect(() => {
    setChangeSheetOpen(false);
  }, [appointmentId]);

  React.useEffect(() => {
    if (!toastMsg) return undefined;
    const timeoutId = window.setTimeout(() => setToastMsg(""), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [toastMsg]);

  React.useEffect(() => {
    if (!location.state?.justRequestedReschedule) return;
    setToastMsg(
      t(
        "doctors.appointmentDetails.toast.rescheduleRequested",
        "New time request sent. Pending for approval."
      )
    );
  }, [location.state]);

  const handleNotesBlur = React.useCallback(async () => {
    const next = String(notesDraft || "").trim();

    try {
      const key = `afrimed:doctor_appt:${String(appointmentId)}`;
      const raw = sessionStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(key, JSON.stringify({ ...parsed, notes: next }));
    } catch {}

    try {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (!uid) return;

      const { error } = await supabase
        .from("appointments")
        .update({ notes: next, updated_at: new Date().toISOString() })
        .eq("id", appointmentId);

      if (error) {
        console.warn("Doctor notes autosave failed:", error);
      }
    } catch (error) {
      console.warn("Doctor notes autosave failed:", error);
    }
  }, [appointmentId, notesDraft]);

  // ---------- hydrated snapshot ----------
  const patient = restored?.patient || restored?.patientProfile || null;
  const routeDoctorFavoriteIds = React.useMemo(
    () =>
      buildFavoriteDoctorIds(restored?.doctor, {
        doctorId:
          restored?.doctor?.id ||
          restored?.doctor?.doctor_id ||
          restored?.doctor?.profile_id ||
          restored?.doctor?.doctor_user_id ||
          restored?.doctor?.user_id ||
          restored?.doctorId ||
          null,
        doctor_id: restored?.doctor?.doctor_id || restored?.doctorId || null,
        doctor_user_id:
          restored?.doctor?.doctor_user_id || restored?.doctor?.user_id || null,
        user_id:
          restored?.doctor?.user_id || restored?.doctor?.doctor_user_id || null,
        profile_id: restored?.doctor?.profile_id || null,
      }),
    [restored?.doctor, restored?.doctorId]
  );

  const patientName =
    patient?.full_name ||
    patient?.name ||
    restored?.patient_name ||
    restored?.patientName ||
    patientFallbackLabel;
  const careSubjectType = String(
    restored?.care_subject_type ||
      restored?.patient?.care_subject_type ||
      restored?.careSubjectType ||
      "self",
  )
    .trim()
    .toLowerCase();
  const guardianName = String(
    restored?.guardian_name ||
      restored?.patient?.guardian_name ||
      restored?.guardianName ||
      "",
  ).trim();
  const responsibleRelationship = String(
    restored?.responsible_relationship ||
      restored?.patient?.responsible_relationship ||
      restored?.care_subject_relationship ||
      restored?.patient?.care_subject_relationship ||
      "",
  ).trim();

  const avatar =
    patient?.avatar_url ||
    patient?.profile_image_url ||
    restored?.patient_avatar ||
    "/icons/circular-avatar-placeholder.svg";

  const pickClinicalValue = (...values) => {
    for (const value of values) {
      if (value === 0 || value === false) return value;
      if (value != null && String(value).trim() !== "") return value;
    }
    return null;
  };

  const sex =
    pickClinicalValue(
      patient?.sex,
      patient?.gender,
      restored?.patientProfile?.sex,
      restored?.patientProfile?.gender,
      restored?.sex,
      restored?.gender,
    ) ?? null;

  const dob =
    pickClinicalValue(
      patient?.dob,
      patient?.date_of_birth,
      patient?.dateOfBirth,
      restored?.ehrSummary?.dob,
      restored?.ehrSummary?.date_of_birth,
      restored?.patientProfile?.dob,
      restored?.patientProfile?.date_of_birth,
      restored?.dob,
      restored?.date_of_birth,
    ) ?? null;

// EHR access status
const [ehrReq, setEhrReq] = React.useState(null);

// patientId for /doctors/patients/:patientId/ehr must be auth user id
const patientId = resolvePatientUserId(
  patient?.user_id,
  restored?.patient_user_id,
  restored?.patient?.user_id,
  restored?.patientProfile?.user_id,
  patient?.id,
  restored?.patientId,
  restored?.patient_id,
  restored?.patient?.id,
);
const doctorUserId = resolvePatientUserId(
  authDoctorUserId,
  restored?.doctor?.user_id,
  restored?.doctor?.doctor_user_id,
  restored?.doctor_id,
  restored?.doctorId,
  restored?.doctor?.doctor_id,
);
const patientUserId = patientId;
const appointment = React.useMemo(
  () => ({ id: String(appointmentId || "").trim() }),
  [appointmentId]
);

const [detailHydration, setDetailHydration] = React.useState({
  appointment: null,
  patientProfile: null,
});

const patientProfilePayload = React.useMemo(
  () => ({
    ...(restored?.patientProfile || {}),
    ...(patient || {}),
    ...(detailHydration.patientProfile || {}),
    id:
      patient?.id ||
      restored?.patientProfile?.id ||
      restored?.patient_row_id ||
      patientId ||
      null,
    user_id:
      patient?.user_id ||
      restored?.patient_user_id ||
      patientId ||
      null,
    full_name:
      detailHydration.patientProfile?.full_name ||
      detailHydration.patientProfile?.display_name ||
      patient?.full_name ||
      patient?.name ||
      restored?.patient_name ||
      restored?.patientName ||
      patientFallbackLabel,
    name:
      detailHydration.patientProfile?.full_name ||
      detailHydration.patientProfile?.display_name ||
      patient?.name ||
      patient?.full_name ||
      restored?.patient_name ||
      restored?.patientName ||
      patientFallbackLabel,
    phone:
      detailHydration.patientProfile?.phone ||
      patient?.phone ||
      restored?.patientProfile?.phone ||
      restored?.patient?.phone ||
      restored?.patient_phone ||
      restored?.patientPhone ||
      null,
    phone_verified_at:
      detailHydration.patientProfile?.phone_verified_at ||
      patient?.phone_verified_at ||
      restored?.patientProfile?.phone_verified_at ||
      restored?.patient?.phone_verified_at ||
      null,
    avatar_url:
      detailHydration.patientProfile?.avatar_url ||
      detailHydration.patientProfile?.profile_image_url ||
      patient?.avatar_url ||
      patient?.profile_image_url ||
      restored?.patient_avatar ||
      "/icons/circular-avatar-placeholder.svg",
    profile_image_url:
      detailHydration.patientProfile?.profile_image_url ||
      detailHydration.patientProfile?.avatar_url ||
      patient?.profile_image_url ||
      patient?.avatar_url ||
      restored?.patient_avatar ||
      detailHydration.patientProfile?.profile_photo_path ||
      "/icons/circular-avatar-placeholder.svg",
    date_of_birth:
      patient?.date_of_birth ||
      patient?.dob ||
      restored?.date_of_birth ||
      restored?.dob ||
      null,
    dob:
      patient?.dob ||
      patient?.date_of_birth ||
      restored?.dob ||
      restored?.date_of_birth ||
      null,
    gender:
      patient?.gender ||
      patient?.sex ||
      restored?.gender ||
      restored?.sex ||
      null,
    sex:
      patient?.sex ||
      patient?.gender ||
      restored?.patientProfile?.sex ||
      restored?.patientProfile?.gender ||
      restored?.sex ||
      restored?.gender ||
      null,
    verification_status:
      detailHydration.patientProfile?.id_verification_status ||
      detailHydration.patientProfile?.verification_status ||
      patient?.id_verification_status ||
      patient?.verification_status ||
      restored?.patientProfile?.id_verification_status ||
      restored?.patientProfile?.verification_status ||
      null,
    id_verification_status:
      detailHydration.patientProfile?.id_verification_status ||
      detailHydration.patientProfile?.verification_status ||
      patient?.id_verification_status ||
      patient?.verification_status ||
      restored?.patientProfile?.id_verification_status ||
      restored?.patientProfile?.verification_status ||
      null,
    blood_type:
      patient?.blood_type ||
      restored?.ehrSummary?.blood_type ||
      restored?.patientProfile?.blood_type ||
      restored?.blood_type ||
      null,
    allergies:
      patient?.allergies ||
      restored?.ehrSummary?.allergies ||
      restored?.patientProfile?.allergies ||
      restored?.allergies ||
      null,
    chronic_conditions:
      patient?.chronic_conditions ||
      patient?.medical_conditions ||
      restored?.ehrSummary?.chronic_conditions ||
      restored?.patientProfile?.chronic_conditions ||
      restored?.patientProfile?.medical_conditions ||
      restored?.chronicConditions ||
      null,
    medical_conditions:
      patient?.medical_conditions ||
      patient?.chronic_conditions ||
      restored?.ehrSummary?.medical_conditions ||
      restored?.ehrSummary?.chronic_conditions ||
      restored?.patientProfile?.medical_conditions ||
      restored?.patientProfile?.chronic_conditions ||
      restored?.chronicConditions ||
      null,
    primary_language:
      patient?.primary_language ||
      restored?.ehrSummary?.primary_language ||
      restored?.patientProfile?.primary_language ||
      null,
    height_cm:
      patient?.height_cm ||
      restored?.ehrSummary?.height_cm ||
      restored?.patientProfile?.height_cm ||
      null,
    weight_kg:
      patient?.weight_kg ||
      restored?.ehrSummary?.weight_kg ||
      restored?.patientProfile?.weight_kg ||
      null,
    current_medications:
      patient?.current_medications ||
      restored?.ehrSummary?.current_medications ||
      restored?.patientProfile?.current_medications ||
      null,
    insurance_provider:
      patient?.insurance_provider ||
      restored?.patientProfile?.insurance_provider ||
      null,
    insurance_policy_number:
      patient?.insurance_policy_number ||
      restored?.patientProfile?.insurance_policy_number ||
      null,
    insurance_member_id:
      patient?.insurance_member_id ||
      restored?.patientProfile?.insurance_member_id ||
      null,
    insurance_plan:
      patient?.insurance_plan ||
      restored?.patientProfile?.insurance_plan ||
      null,
    street_address:
      patient?.street_address ||
      restored?.patientProfile?.street_address ||
      null,
    city:
      patient?.city ||
      restored?.patientProfile?.city ||
      null,
    postal_code:
      patient?.postal_code ||
      restored?.patientProfile?.postal_code ||
      null,
    country:
      patient?.country ||
      restored?.patientProfile?.country ||
      null,
    languages:
      patient?.languages ||
      patient?.primary_language ||
      restored?.patientProfile?.languages ||
      restored?.patientProfile?.primary_language ||
      null,
    organ_donor:
      pickClinicalValue(
        patient?.organ_donor,
        patient?.is_organ_donor,
        restored?.ehrSummary?.organ_donor,
        restored?.patientProfile?.organ_donor,
        restored?.patientProfile?.is_organ_donor,
        restored?.organDonor,
      ) ?? null,
    is_organ_donor:
      pickClinicalValue(
        patient?.is_organ_donor,
        patient?.organ_donor,
        restored?.ehrSummary?.organ_donor,
        restored?.patientProfile?.is_organ_donor,
        restored?.patientProfile?.organ_donor,
        restored?.organDonor,
      ) ?? null,
    patient_photo_paths:
      patient?.patient_photo_paths ||
      restored?.patientProfile?.patient_photo_paths ||
      restored?.patientPhotos ||
      [],
  }),
  [detailHydration.patientProfile, patient, patientFallbackLabel, patientId, restored]
);

const resolvedPatientName =
  patientProfilePayload?.full_name || patientProfilePayload?.name || patientName;
const resolvedPatientAvatar =
  patientProfilePayload?.avatar_url ||
  patientProfilePayload?.profile_image_url ||
  "/icons/circular-avatar-placeholder.svg";

  React.useEffect(() => {
    let alive = true;

    async function hydrateDetailFallbacks() {
      const patientRef = String(
        patient?.user_id ||
          restored?.patient_user_id ||
          restored?.patient?.user_id ||
          restored?.patientProfile?.user_id ||
          patientId ||
          restored?.patient_id ||
          restored?.patient?.id ||
          ""
      ).trim();

      let appointmentRow = null;
      let facilityRow = null;
      let patientLinkRow = null;
      let patientProfileRow = null;
      let ehrInfoRow = null;

      try {
        const { data } = await supabase
          .from("appointments")
          .select(
            "id, appointment_date, duration_minutes, status, consultation_type, practice_id, is_paid, payment_intent_id, booked_service_name, booked_service_price, booked_service_currency"
          )
          .eq("id", String(appointmentId || "").trim())
          .maybeSingle();
        appointmentRow = data || null;
      } catch (error) {
        console.warn("Appointment fallback load failed:", error);
      }

      const practiceId = String(appointmentRow?.practice_id || "").trim();

      if (practiceId) {
        try {
          const { data } = await supabase
            .from("doctor_consultancies")
            .select("id, facility_name, facility_address, facility_city, facility_country")
            .eq("id", practiceId)
            .maybeSingle();
          facilityRow = data || null;
        } catch (error) {
          console.warn("Appointment facility fallback load failed:", error);
        }
      }

      if (patientRef && isUuidLike(patientRef)) {
        try {
          const { data } = await supabase
            .from("patients")
            .select("id, user_id")
            .or(`id.eq.${patientRef},user_id.eq.${patientRef}`)
            .maybeSingle();
          patientLinkRow = data || null;
        } catch (error) {
          console.warn("Patient link fallback load failed:", error);
        }
      }

      const patientRowId = String(patientLinkRow?.id || "").trim();
      const patientUserId = String(patientLinkRow?.user_id || patientRef || "").trim();

      if (patientUserId && isUuidLike(patientUserId)) {
        const [patientProfileResult, ehrInfoByUserResult, ehrInfoByPatientRowResult] = await Promise.allSettled([
          supabase.from("patient_profiles").select("*").eq("user_id", patientUserId).maybeSingle(),
          supabase
            .from("ehr_patient_info")
            .select(
              "patient_id, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor"
            )
            .eq("patient_id", patientUserId)
            .maybeSingle(),
          patientRowId && isUuidLike(patientRowId)
            ? supabase
                .from("ehr_patient_info")
                .select(
                  "patient_id, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor"
                )
                .eq("patient_id", patientRowId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);

        patientProfileRow =
          patientProfileResult.status === "fulfilled" ? patientProfileResult.value?.data || null : null;
        const ehrInfoByUser =
          ehrInfoByUserResult.status === "fulfilled"
            ? ehrInfoByUserResult.value?.data || null
            : null;
        const ehrInfoByPatientRow =
          ehrInfoByPatientRowResult.status === "fulfilled"
            ? ehrInfoByPatientRowResult.value?.data || null
            : null;
        ehrInfoRow = ehrInfoByUser || ehrInfoByPatientRow;
      }

      const mergedPatientProfile = {
        ...(patientProfileRow || {}),
        ...(ehrInfoRow
          ? {
              primary_language:
                ehrInfoRow.primary_language || patientProfileRow?.primary_language || null,
              height_cm: ehrInfoRow.height_cm ?? patientProfileRow?.height_cm ?? null,
              weight_kg: ehrInfoRow.weight_kg ?? patientProfileRow?.weight_kg ?? null,
              blood_type: ehrInfoRow.blood_type || patientProfileRow?.blood_type || null,
              current_medications:
                ehrInfoRow.current_medications || patientProfileRow?.current_medications || null,
              allergies: ehrInfoRow.allergies || patientProfileRow?.allergies || null,
              chronic_conditions: ehrInfoRow.chronic_conditions || null,
              medical_conditions:
                patientProfileRow?.medical_conditions || ehrInfoRow.chronic_conditions || null,
              organ_donor:
                typeof patientProfileRow?.organ_donor === "boolean"
                  ? patientProfileRow.organ_donor
                  : typeof ehrInfoRow?.is_organ_donor === "boolean"
                    ? ehrInfoRow.is_organ_donor
                    : null,
              is_organ_donor:
                typeof ehrInfoRow?.is_organ_donor === "boolean"
                  ? ehrInfoRow.is_organ_donor
                  : typeof patientProfileRow?.organ_donor === "boolean"
                    ? patientProfileRow.organ_donor
                    : null,
            }
          : {}),
      };

      if (!alive) return;
      setDetailHydration({
        appointment: appointmentRow
          ? {
              ...appointmentRow,
              facilityName:
                facilityRow?.facility_name ||
                restored?.facilityName ||
                restored?.facility?.name ||
                null,
              facility:
                facilityRow || restored?.facility
                  ? {
                      id: facilityRow?.id || practiceId || restored?.facility?.id || null,
                      name:
                        facilityRow?.facility_name ||
                        restored?.facility?.name ||
                        restored?.facilityName ||
                        "",
                      address: facilityRow?.facility_address || restored?.facility?.address || "",
                      city: facilityRow?.facility_city || restored?.facility?.city || "",
                      country:
                        facilityRow?.facility_country || restored?.facility?.country || "",
                    }
                  : null,
            }
          : null,
        patientProfile: Object.keys(mergedPatientProfile).length > 0 ? mergedPatientProfile : null,
      });
    }

    hydrateDetailFallbacks();
    return () => {
      alive = false;
    };
  }, [appointmentId, patientId]);

const ehrAccessStatus = ehrStatusFromServer(ehrReq);
const ehrIsRequested = ehrAccessStatus === "requested";
const ehrIsGranted = ehrAccessStatus === "granted";

React.useEffect(() => {
  let alive = true;

  async function loadEhrRequest() {
    try {
      if (!appointmentId) {
        if (alive) setEhrReq(null);
        return;
      }

      // 1) Prefer real auth doctor_id in prod
      let doctorId = null;
      try {
        const { data: authData } = await supabase.auth.getUser();
        doctorId = authData?.user?.id || null;
      } catch {
        doctorId = null;
      }

      // 2) DEV fallback: if auth is missing, use the appointment’s doctor_id so you can sign off UI
      // (Does NOT change prod behavior when auth exists.)
      if (!doctorId && import.meta?.env?.DEV) {
        doctorId =
          restored?.doctor_id ||
          restored?.doctorId ||
          restored?.doctor?.id ||
          restored?.appointment?.doctor_id ||
          null;
      }

      if (!doctorId) {
        if (alive) setEhrReq(null);
        return;
      }

      if (!patientId) {
        if (alive) setEhrReq(null);
        return;
      }

      const canonicalPatientId = await resolvePatientAuthUserId({
        appointmentId,
        patientUserId: patientId,
        patientRef:
          restored?.patient_row_id ||
          restored?.patient?.patient_id ||
          restored?.patientProfile?.patient_id ||
          restored?.patient_id ||
          null,
      });

      if (!canonicalPatientId) {
        if (alive) setEhrReq(null);
        return;
      }

      const nextState = await loadAppointmentEhrAccessState({
        appointmentId,
        doctorId,
        doctorRef: doctorId,
        patientUserId: canonicalPatientId,
        patientRef:
          restored?.patient_row_id ||
          restored?.patient?.patient_id ||
          restored?.patientProfile?.patient_id ||
          restored?.patient_id ||
          canonicalPatientId,
      });

      if (alive) {
        setEhrReq(
          nextState?.status
            ? {
                ...(nextState.request || {}),
                ...(nextState.permission || {}),
                status: nextState.status,
              }
            : null
        );
      }
    } catch (e) {
      console.warn("EHR request load failed:", e);
      if (alive) setEhrReq(null);
    }
  }

  loadEhrRequest();

  return () => {
    alive = false;
  };
}, [appointmentId, restored, patientId]);

const computedAge = calcAge(dob);

// Accept number OR string like "31 Years old" / "31"
const numericAgeRaw =
  patient?.age ??
  patient?.patient_age ??
  restored?.age ??
  restored?.patient_age ??
  null;

const numericAge =
  typeof numericAgeRaw === "number"
    ? numericAgeRaw
    : typeof numericAgeRaw === "string"
      ? Number.parseInt(numericAgeRaw, 10)
      : null;

const age = computedAge ?? (Number.isFinite(numericAge) ? numericAge : null);

  const appointmentDateTime = firstMeaningfulText(
    [
      detailHydration.appointment?.appointment_date,
      restored?.appointment_date,
      mergedSnapshot?.appointment_date,
      dbSnapshot?.appointment_date,
    ],
    ""
  );
  const appointmentDurationMinutes = firstDefinedValue(
    [
      detailHydration.appointment?.duration_minutes,
      restored?.duration_minutes,
      restored?.durationMinutes,
      restored?.when?.duration_mins,
      mergedSnapshot?.duration_minutes,
      dbSnapshot?.duration_minutes,
    ],
    0
  );
  const status = normalizeStatus(
    detailHydration.appointment?.status || restored?.status || mergedSnapshot?.status
  );
  const visitType = normalizeVisitType({
    ...(restored || {}),
    consultation_type:
      detailHydration.appointment?.consultation_type ||
      restored?.consultation_type ||
      mergedSnapshot?.consultation_type ||
      dbSnapshot?.consultation_type ||
      null,
  });
  const facilityName = firstMeaningfulText(
    [
      detailHydration.appointment?.facilityName,
      detailHydration.appointment?.facility?.name,
      restored?.facilityName,
      restored?.facility?.name,
      restored?.facility_name,
      mergedSnapshot?.facilityName,
      mergedSnapshot?.facility?.name,
      dbSnapshot?.facilityName,
      dbSnapshot?.facility?.name,
    ],
    t("doctors.appointmentDetails.facilityFallback", "Life Gaborone Private Hospital")
  );

  const startTime = firstMeaningfulText(
    [
      appointmentDateToHHMM(appointmentDateTime),
      restored?.start,
      restored?.selectedTime,
      restored?.when?.time,
      mergedSnapshot?.start,
      mergedSnapshot?.selectedTime,
      mergedSnapshot?.when?.time,
      dbSnapshot?.start,
      dbSnapshot?.selectedTime,
      dbSnapshot?.when?.time,
    ],
    ""
  );

  const endTime = firstMeaningfulText(
    [
      appointmentDateToEndHHMM(appointmentDateTime, appointmentDurationMinutes),
      restored?.end,
      restored?.endTime,
      restored?.when?.end,
      mergedSnapshot?.end,
      mergedSnapshot?.endTime,
      mergedSnapshot?.when?.end,
      dbSnapshot?.end,
      dbSnapshot?.endTime,
      dbSnapshot?.when?.end,
    ],
    ""
  );

  const dateISO = firstMeaningfulText(
    [
      appointmentDateToISO(appointmentDateTime),
      restored?.dateISO,
      restored?.selectedDate,
      restored?.when?.dateISO,
      restored?.when?.date,
      mergedSnapshot?.dateISO,
      mergedSnapshot?.selectedDate,
      mergedSnapshot?.when?.dateISO,
      dbSnapshot?.dateISO,
      dbSnapshot?.selectedDate,
      dbSnapshot?.when?.dateISO,
    ],
    ""
  );

  const dateLabel = dateISO ? formatLongDateLabel(dateISO, localeCode, dashLabel) : dashLabel;
  const compactDateLabel = dateISO
    ? formatCompactLongDate(dateISO, localeCode, dashLabel)
    : dashLabel;
  const startTimeLabel = formatDisplayTime(startTime, localeCode) || dashLabel;
  const endTimeLabel = formatDisplayTime(endTime, localeCode) || dashLabel;
  const accessLayerSmsPreviewVisible = canShowAccessLayerSmsPreview(location.search);
  const accessLayerSmsProofSendVisible = canShowAccessLayerSmsProofSend(location.search);
  const accessLayerRecipientPhone = firstMeaningfulText(
    [
      patientProfilePayload?.phone,
      detailHydration.patientProfile?.phone,
      patient?.phone,
      restored?.patientProfile?.phone,
      restored?.patient?.phone,
      restored?.patient_phone,
      restored?.patientPhone,
    ],
    ""
  );
  const accessLayerSmsPreview = React.useMemo(
    () =>
      buildAppointmentSmsConfirmation({
        appointment: {
          id: appointmentId,
          appointment_date: appointmentDateTime,
          patientPhone: accessLayerRecipientPhone,
        },
      }),
    [appointmentDateTime, appointmentId, accessLayerPreviewVersion, accessLayerRecipientPhone]
  );
  const accessLayerProviderState = accessLayerSmsProofSendVisible
    ? "twilio_test_proof_available"
    : accessLayerSmsPreview?.provider || "provider_not_configured";

  React.useEffect(() => {
    if (!accessLayerSmsProofSendVisible || !appointmentId) {
      setAccessLayerSmsDispatchEvidence(null);
      setAccessLayerSmsDispatchEvidenceStatus("idle");
      return undefined;
    }

    let alive = true;

    async function loadAccessLayerDispatchEvidence() {
      setAccessLayerSmsDispatchEvidenceStatus("loading");
      const { data, error } = await supabase
        .from("communication_dispatches")
        .select(
          [
            "id",
            "channel",
            "purpose",
            "recipient_phone",
            "appointment_id",
            "message_body",
            "reference_code",
            "provider",
            "provider_message_id",
            "status",
            "is_test",
            "metadata_json",
            "error_code",
            "error_message",
            "created_at",
            "sent_at",
          ].join(",")
        )
        .eq("appointment_id", appointmentId)
        .eq("channel", "sms")
        .eq("purpose", "appointment_confirmation_test_sms")
        .eq("is_test", true)
        .order("created_at", { ascending: false })
        .limit(1);

      if (!alive) return;
      if (error) {
        setAccessLayerSmsDispatchEvidence(null);
        setAccessLayerSmsDispatchEvidenceStatus("error");
        return;
      }

      setAccessLayerSmsDispatchEvidence(Array.isArray(data) ? data[0] || null : null);
      setAccessLayerSmsDispatchEvidenceStatus("loaded");
    }

    loadAccessLayerDispatchEvidence();

    return () => {
      alive = false;
    };
  }, [
    accessLayerSmsProofSendVisible,
    accessLayerPreviewVersion,
    accessLayerSmsSendResult?.dispatchId,
    appointmentId,
  ]);

  const apptTypeLabel = firstMeaningfulText(
    [
      detailHydration.appointment?.booked_service_name,
      String(detailHydration.appointment?.consultation_type || "").toLowerCase() === "private"
        ? eVisitLabel
        : "",
      restored?.serviceName,
      restored?.booked_service_name,
      restored?.serviceLabel,
      restored?.service_name,
      restored?.subtitle,
      restored?.appointment_type,
      restored?.typeLabel,
      mergedSnapshot?.serviceName,
      mergedSnapshot?.booked_service_name,
      mergedSnapshot?.serviceLabel,
      mergedSnapshot?.subtitle,
      dbSnapshot?.serviceName,
      dbSnapshot?.booked_service_name,
      dbSnapshot?.serviceLabel,
    ],
    firstAppointmentLabel
  );

  const resolvedPriceAmount = firstPositiveAmount(
    detailHydration.appointment?.booked_service_price,
    restored?.booked_service_price,
    restored?.amountPaid,
    restored?.amount_paid,
    mergedSnapshot?.booked_service_price,
    mergedSnapshot?.amountPaid,
    mergedSnapshot?.amount_paid,
    dbSnapshot?.booked_service_price,
    dbSnapshot?.amountPaid,
    dbSnapshot?.amount_paid
  );
  const resolvedPriceCurrency =
    detailHydration.appointment?.booked_service_currency ||
    restored?.booked_service_currency ||
    restored?.currency ||
    mergedSnapshot?.booked_service_currency ||
    mergedSnapshot?.currency ||
    dbSnapshot?.booked_service_currency ||
    dbSnapshot?.currency ||
    "ZAR";
  const consultationPriceLabel =
    resolvedPriceAmount != null
      ? `${resolvedPriceAmount.toFixed(2)} ${resolvedPriceCurrency}`
      : normalizePriceLabel(
          firstDefinedValue(
            [
              cleanPriceText(restored?.serviceNamePrice),
              cleanPriceText(restored?.service_name_price),
              cleanPriceText(restored?.doctor?.serviceNamePrice),
              cleanPriceText(mergedSnapshot?.serviceNamePrice),
              cleanPriceText(mergedSnapshot?.service_name_price),
              cleanPriceText(dbSnapshot?.serviceNamePrice),
              cleanPriceText(dbSnapshot?.service_name_price),
            ],
            "—"
          ),
          resolvedPriceCurrency
        );

  const patientVerificationStatus = normalizeVerificationStatus(
    firstDefinedValue(
      [
        detailHydration.patientProfile?.id_verification_status,
        detailHydration.patientProfile?.verification_status,
        patientProfilePayload?.id_verification_status,
        patientProfilePayload?.verification_status,
        patient?.id_verification_status,
        patient?.verification_status,
        restored?.patient?.id_verification_status,
        restored?.patient?.verification_status,
        restored?.patientProfile?.id_verification_status,
        restored?.patientProfile?.verification_status,
      ],
      ""
    ),
    "pending"
  );
  const patientVerificationIcon =
    patientVerificationStatus === "verified"
      ? ICONS.verified
      : ICONS.pendingVerification;

  const meetUrl =
    restored?.meetUrl ||
    restored?.meet_url ||
    "";
  const meetUrlLabel = meetPendingLabel;

  const notes =
    restored?.notes ||
    restored?.diagnosisNotes ||
    restored?.patient_notes ||
    "";
  const diagnosisTitleRaw =
    restored?.diagnosisTitle ||
    restored?.diagnosis ||
    "";
  const diagnosisTitle = diagnosisTitleRaw || diagnosisTitleFallback;
  const diagnosisBody =
    restored?.diagnosisNotes ||
    restored?.notes ||
    "";
  const vitalRepSourceSnapshot = React.useMemo(
    () =>
      buildVitalRepSourceSnapshot({
        appointmentId,
        restoredSnapshot: restoredWithPhotos || restored,
        diagnosisTitle: diagnosisTitleRaw,
        diagnosisNotes: diagnosisBody,
        notes,
      }),
    [appointmentId, diagnosisBody, diagnosisTitleRaw, notes, restored, restoredWithPhotos]
  );

  const updateVitalRepStructuredField = React.useCallback((field, value) => {
    setVitalRepStructuredData((current) => ({
      ...normalizeVitalRepStructuredClinicalData(current),
      [field]: String(value || ""),
    }));
  }, []);

  const updateVitalRepStructuredVital = React.useCallback((field, value) => {
    setVitalRepStructuredData((current) => {
      const normalized = normalizeVitalRepStructuredClinicalData(current);
      return {
        ...normalized,
        vitals: {
          ...normalized.vitals,
          [field]: String(value || ""),
        },
      };
    });
  }, []);

  const [clockNow, setClockNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockNow(Date.now());
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const bloodTypeRaw =
    pickClinicalValue(
      patientProfilePayload?.blood_type,
      restored?.bloodType,
      restored?.blood_type,
      restored?.ehr?.bloodType,
      restored?.ehr?.blood_type,
      restored?.ehrSummary?.blood_type,
      restored?.ehrSummary?.bloodType,
      detailHydration.patientProfile?.blood_type,
      patient?.blood_type,
      restored?.patientProfile?.blood_type,
      mergedSnapshot?.ehrSummary?.blood_type,
      mergedSnapshot?.patient?.blood_type,
      mergedSnapshot?.patientProfile?.blood_type,
      dbSnapshot?.ehrSummary?.blood_type,
      dbSnapshot?.patient?.blood_type,
      dbSnapshot?.patientProfile?.blood_type,
    ) ?? dashLabel;
  const allergiesRaw =
    pickClinicalValue(
      patientProfilePayload?.allergies,
      restored?.allergies,
      restored?.ehr?.allergies,
      restored?.ehrSummary?.allergies,
      detailHydration.patientProfile?.allergies,
      patient?.allergies,
      restored?.patientProfile?.allergies,
      mergedSnapshot?.ehrSummary?.allergies,
      mergedSnapshot?.patient?.allergies,
      mergedSnapshot?.patientProfile?.allergies,
      dbSnapshot?.ehrSummary?.allergies,
      dbSnapshot?.patient?.allergies,
      dbSnapshot?.patientProfile?.allergies,
    ) ?? dashLabel;
  const chronicRaw =
    pickClinicalValue(
      patientProfilePayload?.chronic_conditions,
      patientProfilePayload?.medical_conditions,
      restored?.chronicConditions,
      restored?.chronic_conditions,
      restored?.ehr?.chronicConditions,
      restored?.ehr?.chronic_conditions,
      restored?.ehr?.medical_conditions,
      restored?.ehrSummary?.chronic_conditions,
      restored?.ehrSummary?.medical_conditions,
      detailHydration.patientProfile?.medical_conditions,
      patient?.chronic_conditions,
      patient?.medical_conditions,
      restored?.patientProfile?.chronic_conditions,
      restored?.patientProfile?.medical_conditions,
      mergedSnapshot?.ehrSummary?.chronic_conditions,
      mergedSnapshot?.ehrSummary?.medical_conditions,
      mergedSnapshot?.patient?.chronic_conditions,
      mergedSnapshot?.patient?.medical_conditions,
      mergedSnapshot?.patientProfile?.chronic_conditions,
      mergedSnapshot?.patientProfile?.medical_conditions,
      dbSnapshot?.ehrSummary?.chronic_conditions,
      dbSnapshot?.ehrSummary?.medical_conditions,
      dbSnapshot?.patient?.chronic_conditions,
      dbSnapshot?.patient?.medical_conditions,
      dbSnapshot?.patientProfile?.chronic_conditions,
      dbSnapshot?.patientProfile?.medical_conditions,
    ) ?? dashLabel;
  const donorRaw =
    pickClinicalValue(
      patientProfilePayload?.is_organ_donor,
      patientProfilePayload?.organ_donor,
      restored?.organDonor,
      restored?.organ_donor,
      restored?.ehr?.organDonor,
      restored?.ehr?.organ_donor,
      restored?.ehrSummary?.organ_donor,
      detailHydration.patientProfile?.organ_donor,
      patient?.is_organ_donor,
      patient?.organ_donor,
      restored?.patientProfile?.is_organ_donor,
      restored?.patientProfile?.organ_donor,
      mergedSnapshot?.ehrSummary?.organ_donor,
      mergedSnapshot?.patient?.is_organ_donor,
      mergedSnapshot?.patient?.organ_donor,
      mergedSnapshot?.patientProfile?.is_organ_donor,
      mergedSnapshot?.patientProfile?.organ_donor,
      dbSnapshot?.ehrSummary?.organ_donor,
      dbSnapshot?.patient?.is_organ_donor,
      dbSnapshot?.patient?.organ_donor,
      dbSnapshot?.patientProfile?.is_organ_donor,
      dbSnapshot?.patientProfile?.organ_donor,
    ) ?? dashLabel;

  const bloodType = displayValue(bloodTypeRaw, dashLabel);
  const allergies = displayValue(allergiesRaw, dashLabel);
  const chronic = displayValue(chronicRaw, dashLabel);
  const donor = displayValue(donorRaw, dashLabel, {
    true: t("common.yes", "Yes"),
    false: t("common.no", "No"),
  });

  const patientMeta = [
    age != null
      ? t("doctors.appointmentDetails.yearsOld", "{count} Years old", { count: age })
      : null,
    (() => {
      const raw = String(sex || "").trim().toLowerCase();
      if (!raw) return null;
      const custom = CUSTOM_LOCALE_LABELS[languageCode]?.sex || {};
      if (raw === "male") return custom.male || "Male";
      if (raw === "female") return custom.female || "Female";
      return custom.other || String(sex).charAt(0).toUpperCase() + String(sex).slice(1);
    })(),
  ]
    .filter(Boolean)
    .join(", ");
  const startsInLabel = getStartsInLabel(dateISO, startTime, clockNow, {
    defaultLabel: startsInDefaultLabel,
    startLabel: startConsultationLabel,
    minutesLabel: (count) =>
      t("doctors.appointmentDetails.startsIn.minutes", "Starts in {count} min", { count }),
    hoursLabel: (count) =>
      t("doctors.appointmentDetails.startsIn.hours", "Starts in {count} hour{suffix}", {
        count,
        suffix: count === 1 ? "" : "s",
      }),
  });
  const consultationStartState = getConsultationStartState(dateISO, startTime, clockNow);
  const appointmentLifecycleBucket = getAppointmentLifecycleBucket(
    {
      appointment_date: appointmentDateTime || null,
      selectedDate: dateISO || null,
      selectedTime: startTime || null,
      consultation_type: visitType === "evisit" ? "private" : "moh",
      status,
    },
    clockNow
  );
  const isPastInteractionDisabled = appointmentLifecycleBucket === "past";
  const appointmentStartMs = appointmentDateTime
    ? new Date(appointmentDateTime).getTime()
    : Number.NaN;
  const videoJoinGraceMs = 15 * 60 * 1000;
  const isWithinVideoJoinWindow =
    visitType === "evisit" &&
    Number.isFinite(appointmentStartMs) &&
    clockNow >= appointmentStartMs &&
    clockNow <= appointmentStartMs + videoJoinGraceMs;
  const mutedSupportIconStyle = {
    filter:
      "brightness(0) saturate(100%) invert(86%) sepia(6%) saturate(240%) hue-rotate(184deg) brightness(98%) contrast(92%)",
  };
  const isStartsSoonEvisitDetails =
    visitType === "evisit" &&
    status === "confirmed" &&
    consultationStartState === "countdown" &&
    !isPastInteractionDisabled;
  const videoStatus = String(
    firstMeaningfulText(
      [
        detailHydration.appointment?.video_status,
        restored?.video_status,
        mergedSnapshot?.video_status,
        dbSnapshot?.video_status,
      ],
      ""
    )
  )
    .trim()
    .toLowerCase();
  const videoStartedAt = firstMeaningfulText(
    [
      detailHydration.appointment?.video_started_at,
      restored?.video_started_at,
      mergedSnapshot?.video_started_at,
      dbSnapshot?.video_started_at,
    ],
    ""
  );
  const videoEndedAt = firstMeaningfulText(
    [
      detailHydration.appointment?.video_ended_at,
      restored?.video_ended_at,
      mergedSnapshot?.video_ended_at,
      dbSnapshot?.video_ended_at,
    ],
    ""
  );
  const consultationEnded =
    visitType === "evisit" &&
    (videoStatus === "completed" ||
      videoStatus === "ended" ||
      videoStatus === "closed" ||
      Boolean(videoEndedAt));
  const isStartConsultationEvisitDetails =
    visitType === "evisit" &&
    status === "confirmed" &&
    consultationStartState === "start" &&
    !isPastInteractionDisabled;
  const isCompleteConsultationEvisitDetails =
    visitType === "evisit" &&
    (status === "completed" ||
      (status === "in_progress" && (consultationEnded || !isWithinVideoJoinWindow)));
  const isBaseEvisitDetails =
    visitType === "evisit" &&
    status === "confirmed" &&
    !isStartsSoonEvisitDetails &&
    !isStartConsultationEvisitDetails;
  const isStaticConfirmedDetails =
    visitType !== "evisit" && (status === "confirmed" || status === "scheduled");
  const isReadOnlyDetailsVariant =
    isBaseEvisitDetails ||
    isStaticConfirmedDetails ||
    isStartsSoonEvisitDetails ||
    isStartConsultationEvisitDetails;
  const showRequestEhrCard = visitType !== "evisit" && !ehrIsRequested && !ehrIsGranted;
  const canOpenLiveCall =
    !isPastInteractionDisabled &&
    visitType === "evisit" &&
    (status === "confirmed" || status === "in_progress") &&
    !consultationEnded &&
    (status === "in_progress" || consultationStartState === "start" || isWithinVideoJoinWindow);
  const isTimedEvisitDetails =
    isStartsSoonEvisitDetails ||
    isStartConsultationEvisitDetails ||
    canOpenLiveCall ||
    isCompleteConsultationEvisitDetails;
  const showTimedRequestCard = false;
  const showCompletionRequestEhrCard = false;
  const ghostActionDisabledClass =
    "disabled:opacity-100 disabled:bg-[rgba(86,106,139,0.08)] disabled:text-[rgb(var(--afrimed-primary))] disabled:cursor-not-allowed disabled:[&_img]:opacity-100";
  const detailsPaymentMethodLabel =
    firstDefinedValue(
      [
        restored?.paymentMethodLabel,
        restored?.paymentLabel,
        prettyPaymentMethod(restored?.paymentMethod, ""),
        mergedSnapshot?.paymentMethodLabel,
        mergedSnapshot?.paymentLabel,
        prettyPaymentMethod(mergedSnapshot?.paymentMethod, ""),
        dbSnapshot?.paymentMethodLabel,
        dbSnapshot?.paymentLabel,
        prettyPaymentMethod(dbSnapshot?.paymentMethod, ""),
        detailHydration.appointment?.is_paid ? t("appointments.edit.paymentMethodFallback", "Debit / Credit cards") : "",
      ],
      ""
    ) || t("appointments.edit.paymentMethodFallback", "Debit / Credit cards");
  const editAppointmentState = {
    ...(restored || {}),
    actorRole: "doctor",
    patient: patient || restored?.patient || null,
    patientId,
    mode: visitType,
    visitType,
    consultation_type: visitType === "evisit" ? "private" : "moh",
    previousMode:
      restored?.previousMode ||
      restored?.previous_mode ||
      restored?.mode ||
      visitType,
    previousConsultationType:
      restored?.previousConsultationType ||
      restored?.previous_consultation_type ||
      restored?.consultation_type ||
      (visitType === "evisit" ? "private" : "moh"),
    selectedDate: dateISO || restored?.selectedDate || restored?.when?.dateISO || "",
    selectedTime: startTime || restored?.selectedTime || restored?.when?.time || "",
    when: {
      ...(restored?.when || {}),
      dateISO: dateISO || restored?.selectedDate || restored?.when?.dateISO || "",
      time: startTime || restored?.selectedTime || restored?.when?.time || "",
      end: endTime || restored?.endTime || restored?.when?.end || "",
      duration_mins:
        appointmentDurationMinutes ||
        restored?.duration_minutes ||
        restored?.durationMinutes ||
        restored?.when?.duration_mins ||
        0,
    },
    duration_minutes:
      appointmentDurationMinutes ||
      restored?.duration_minutes ||
      restored?.durationMinutes ||
      restored?.when?.duration_mins ||
      0,
    durationMinutes:
      appointmentDurationMinutes ||
      restored?.durationMinutes ||
      restored?.duration_minutes ||
      restored?.when?.duration_mins ||
      0,
    facilityId:
      restored?.facilityId ||
      restored?.facility?.id ||
      restored?.practice_id ||
      detailHydration.appointment?.practice_id ||
      null,
    facilityName,
    facility:
      restored?.facility ||
      detailHydration.appointment?.facility ||
      (facilityName ? { name: facilityName } : null),
    booked_service_name: apptTypeLabel || restored?.booked_service_name || null,
    booked_service_price:
      resolvedPriceAmount != null ? resolvedPriceAmount : restored?.booked_service_price ?? null,
    booked_service_currency:
      resolvedPriceCurrency || restored?.booked_service_currency || restored?.currency || null,
    serviceName: apptTypeLabel || restored?.serviceName || restored?.serviceLabel || "",
    serviceLabel: apptTypeLabel || restored?.serviceLabel || restored?.serviceName || "",
    serviceNamePrice: consultationPriceLabel !== dashLabel ? consultationPriceLabel : "",
    amountPaid: resolvedPriceAmount != null ? resolvedPriceAmount : restored?.amountPaid ?? null,
    currency: resolvedPriceCurrency || restored?.currency || null,
    paymentMethodLabel: detailsPaymentMethodLabel,
    paymentLabel: detailsPaymentMethodLabel,
  };
  const snapshotPrescriptionId = String(
    restored?.prescriptionId ||
      restored?.prescription?.id ||
      ""
  ).trim();
  const prescriptionMetaFromSnapshot =
    restored?.prescription?.meta && typeof restored.prescription.meta === "object"
      ? restored.prescription.meta
      : null;
  const [prescriptionId, setPrescriptionId] = React.useState(snapshotPrescriptionId);
  const [prescriptionMeta, setPrescriptionMeta] = React.useState(
    prescriptionMetaFromSnapshot
  );
  const vitalRepStatus = String(vitalRepNote?.status || "").trim().toLowerCase();
  const vitalRepHasDraft = Boolean(
    String(vitalRepNote?.generatedDraftText || "").trim() ||
      String(vitalRepNote?.sourceTranscript || "").trim() ||
      vitalRepNote?.soapDraft
  );
  const vitalRepHasFinal = ["finalized", "final"].includes(vitalRepStatus) && Boolean(
    String(vitalRepNote?.finalNoteText || "").trim()
  );
  const vitalRepHasStructuredData = hasVitalRepStructuredClinicalData(vitalRepStructuredData);
  const canShowVitalRepAction = Boolean(appointmentId);
  const vitalRepIsLoading = Boolean(appointmentId) && !vitalRepHydrated;

  React.useEffect(() => {
    setVitalRepAmendmentDraft(readVitalRepAmendmentDraft(appointmentId));
  }, [appointmentId, vitalRepNote?.updatedAt, vitalRepStatus]);

  React.useEffect(() => {
    setPrescriptionId(snapshotPrescriptionId);
  }, [snapshotPrescriptionId]);

  React.useEffect(() => {
    setPrescriptionMeta(prescriptionMetaFromSnapshot);
  }, [prescriptionMetaFromSnapshot]);

  React.useEffect(() => {
    let alive = true;
    setVitalRepHydrated(false);

    async function hydrateVitalRepNote() {
      try {
        const nextNote = await loadVitalRepNoteByAppointment(appointmentId);
        if (!alive) return;
        setVitalRepNote(nextNote);
        setVitalRepStructuredData(
          nextNote
            ? buildVitalRepStructuredDataFromNote(nextNote, vitalRepSourceSnapshot, prescriptionMeta)
            : buildInitialVitalRepStructuredClinicalData(vitalRepSourceSnapshot, prescriptionMeta)
        );
        setVitalRepDraftText(
          String(nextNote?.finalNoteText || nextNote?.generatedDraftText || "").trim()
        );
      } catch {
        if (!alive) return;
        setVitalRepNote(null);
        setVitalRepStructuredData(
          buildInitialVitalRepStructuredClinicalData(vitalRepSourceSnapshot, prescriptionMeta)
        );
        setVitalRepDraftText("");
      } finally {
        if (alive) setVitalRepHydrated(true);
      }
    }

    hydrateVitalRepNote();
    const handleVitalRepChanged = () => {
      hydrateVitalRepNote();
    };
    window.addEventListener("afrimed:vitalrep:changed", handleVitalRepChanged);
    return () => {
      alive = false;
      window.removeEventListener("afrimed:vitalrep:changed", handleVitalRepChanged);
    };
  }, [appointmentId, prescriptionMeta, vitalRepSourceSnapshot]);

  const completeConsultationDialogBody = ehrIsGranted
    ? t(
        "doctors.appointmentDetails.completeConsultationDialog.bodyWithEhr",
        "The patient's EHR will no longer be accessible. To access the patient's EHR again, you will need to Request Access."
      )
    : t(
        "doctors.appointmentDetails.completeConsultationDialog.bodyWithoutEhr",
        "This will mark the consultation as complete for this appointment."
      );

  React.useEffect(() => {
    let alive = true;

    async function hydrateLatestPrescription() {
      try {
        let nextPrescriptionId = String(snapshotPrescriptionId || "").trim();
        let nextPrescriptionMeta =
          prescriptionMetaFromSnapshot && typeof prescriptionMetaFromSnapshot === "object"
            ? prescriptionMetaFromSnapshot
            : null;

        if (appointmentId) {
          const { data: latestPrescription, error: latestPrescriptionError } = await supabase
            .from("prescriptions")
            .select("id, meta, created_at")
            .eq("appointment_id", appointmentId)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (!latestPrescriptionError && latestPrescription?.id) {
            nextPrescriptionId = String(latestPrescription.id || "").trim();
            nextPrescriptionMeta =
              latestPrescription?.meta && typeof latestPrescription.meta === "object"
                ? latestPrescription.meta
                : nextPrescriptionMeta;
          }
        }

        if (!alive) return;

        setPrescriptionId(nextPrescriptionId);
        if (nextPrescriptionMeta && typeof nextPrescriptionMeta === "object") {
          setPrescriptionMeta(nextPrescriptionMeta);
        }
      } catch {
        if (!alive) return;
      }
    }

    hydrateLatestPrescription();
    return () => {
      alive = false;
    };
  }, [appointmentId, prescriptionMetaFromSnapshot, snapshotPrescriptionId]);

  const detailPlaceholderState = !restored && snapshotLoading ? "loading" : !restored ? "not-found" : "";

  const statusPresentation = getStatusPresentation(
    isStaticConfirmedDetails ? "confirmed" : status,
    {
      pending: t("doctors.appointmentDetails.status.pending", "Pending"),
      scheduled: t("doctors.appointmentDetails.status.scheduled", "Scheduled"),
      inProgress: t("doctors.appointmentDetails.status.inProgress", "In progress"),
      completed: t("doctors.appointmentDetails.status.completed", "Completed"),
      missed: t("doctors.appointmentDetails.status.missed", "Missed"),
      cancelled: t("doctors.appointmentDetails.status.cancelled", "Cancelled"),
      confirmed: t("doctors.appointmentDetails.status.confirmed", "Confirmed"),
    }
  );
  const photoPreview =
    Array.isArray(restoredWithPhotos?.patientPhotos) && restoredWithPhotos.patientPhotos.length
      ? restoredWithPhotos.patientPhotos.slice(0, 4)
      : [];
  const labItems = buildLabItems(restoredWithPhotos || restored || {}, photoPreview);
  const hasDiagnosisContent = Boolean(vitalRepNote?.authorityReportId);
  const diagnosisClinicalDisplaySections = buildDiagnosisClinicalDisplaySections({
    diagnosisTitle,
    diagnosisBody,
    appointmentReason: apptTypeLabel,
    labels: {
      clinicalImpression: t(
        "doctors.appointmentDetails.diagnosisDisplay.clinicalImpression",
        "Diagnosis and clinical impression"
      ),
      notRecorded: t("doctors.appointmentDetails.diagnosisDisplay.notRecorded", "Not recorded."),
    },
  });
  const prescriptionLabel =
    firstDefinedValue(
      [
        restored?.prescription?.medicineName,
        restored?.prescription?.name,
        restored?.prescription?.meta?.medicineName,
        restored?.prescription?.meta?.name,
      ],
      prescriptionSectionLabel
    ) || prescriptionSectionLabel;
  const hasIssuedPrescription = Boolean(prescriptionId);
  const hasDocumentContent = labItems.length > 0;
  const trimmedNotesDraft = String(notesDraft || "").trim();
  const hasSavedNotes = Boolean(trimmedNotesDraft);
  const consultationAlreadyCompleted = status === "completed";
  const consultationIncomplete = status === "incomplete";
  const canCompleteConsultation = status === "in_progress" && hasDiagnosisContent && hasSavedNotes;
  // Referral is a post-consultation action. A past calendar date alone is not
  // evidence that a cancelled, missed or incomplete encounter took place.
  const canShowReferralActions = Boolean(
    appointment.id && doctorUserId && patientUserId && status === "completed"
  );
  const vitalRepCtaLabel = vitalRepHasFinal
      ? amendVitalRepLabel
      : vitalRepHasDraft
      ? reviewVitalRepLabel
      : generateVitalRepLabel;
  const vitalRepStatusLabel = vitalRepHasFinal
    ? vitalRepSavedLabel
    : vitalRepHasDraft
      ? vitalRepDraftReadyLabel
      : "";

  React.useEffect(() => {
    setVitalRepPreviewOpen(false);
  }, [appointmentId]);

  const ehrAccessSurface = (
    <div className="mt-7">
      {showRequestEhrCard ? (
        <Card>
          <div className="flex min-h-[116px] items-center justify-between gap-4 px-4 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-title-medium text-on-surface">{requestEhrLabel}</div>
              <div className="mt-1 text-body-medium leading-5 text-on-surface-var">
                {requestEhrBodyLabel}
              </div>
              <button
                type="button"
                onClick={requestEhrAccess}
                className="mt-4 text-label-large text-[rgb(var(--afrimed-primary))]"
              >
                {requestLabel}
              </button>
            </div>
            <img
              src={ICONS.ehrTonal}
              alt=""
              className="h-16 w-16 shrink-0 self-center"
              draggable="false"
            />
          </div>
        </Card>
      ) : (
        <>
          <div className="mb-3 text-title-medium text-on-surface-var">{ehrLabel}</div>
          <Card className="overflow-hidden">
            {ehrIsGranted ? (
              <>
                <ListRow
                  icon={ICONS.ehrTonal}
                  label={
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{ehrLabel}</span>
                      <span className="shrink-0 text-body-medium text-on-surface-var">
                        {accessGrantedLabel}
                      </span>
                    </span>
                  }
                  value={null}
                  clickable
                  onClick={openEhrDetails}
                  right={
                    <div className="flex items-center gap-1 text-body-large text-on-surface-var">
                      <span>{detailsLabel}</span>
                      <img src={ICONS.next} alt="" className="h-6 w-6 shrink-0" draggable="false" />
                    </div>
                  }
                />
                <Divider inset variant="outlineVariant" />
                <ListRow icon={ICONS.blood} label={bloodTypeLabel} value={null} right={<span className="text-body-large text-on-surface-var">{bloodType}</span>} />
                <Divider inset variant="outlineVariant" />
                <ListRow icon={ICONS.allergies} label={allergiesLabel} value={null} right={<span className="text-body-large text-on-surface-var">{allergies}</span>} />
                <Divider inset variant="outlineVariant" />
                <ListRow icon={ICONS.chronic} label={chronicConditionsLabel} value={null} right={<span className="text-body-large text-on-surface-var">{chronic}</span>} />
                <Divider inset variant="outlineVariant" />
                <ListRow icon={ICONS.donor} label={organDonorLabel} value={null} right={<span className="text-body-large text-on-surface-var">{donor}</span>} />
              </>
            ) : (
              <ListRow
                icon={ICONS.ehrTonal}
                label={ehrLabel}
                value={null}
                clickable={false}
                right={
                  <div className="flex items-center gap-2 text-body-large text-on-surface-var">
                    <span>{accessRequestedLabel}</span>
                    <img src={ICONS.ehrRequestPending} alt="" className="h-6 w-6 shrink-0" draggable="false" />
                  </div>
                }
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
  const referralHandoverRow = (
    <div className="mt-4">
      <Card className="overflow-hidden">
        <CareActionRow
          icon={ICONS.diagnosis}
          title={referralHandoverLabel}
          body={referralHandoverBodyLabel}
          actionLabel={openLabel}
          onClick={openReferralHandover}
          iconStyle={appointmentActionIconStyle}
        />
      </Card>
    </div>
  );

  const vitalRepPreviewText = String(
    vitalRepNote?.finalNoteText || vitalRepNote?.generatedDraftText || ""
  ).trim();
  const vitalRepVitalsSourceOptions = [
    {
      value: VITALREP_VITALS_SOURCE.NOT_CAPTURED,
      label: t("doctors.appointmentDetails.vitalRep.vitalsSource.notCaptured", "Not captured"),
    },
    {
      value: VITALREP_VITALS_SOURCE.CLINICIAN_CAPTURED,
      label: t("doctors.appointmentDetails.vitalRep.vitalsSource.clinicianCaptured", "Clinician captured"),
    },
    {
      value: VITALREP_VITALS_SOURCE.PATIENT_REPORTED,
      label: t("doctors.appointmentDetails.vitalRep.vitalsSource.patientReported", "Patient reported"),
    },
    {
      value: VITALREP_VITALS_SOURCE.HOME_DEVICE,
      label: t("doctors.appointmentDetails.vitalRep.vitalsSource.homeDevice", "Home device"),
    },
    {
      value: VITALREP_VITALS_SOURCE.UPLOADED_DOCUMENT,
      label: t("doctors.appointmentDetails.vitalRep.vitalsSource.uploadedDocument", "Uploaded document"),
    },
  ];
  const vitalRepVitalFields = [
    {
      key: "bloodPressure",
      label: t("doctors.appointmentDetails.vitalRep.fields.bloodPressure", "Blood pressure"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.bloodPressure", "Enter blood pressure"),
    },
    {
      key: "heartRate",
      label: t("doctors.appointmentDetails.vitalRep.fields.heartRate", "Heart rate"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.heartRate", "Enter heart rate"),
    },
    {
      key: "temperature",
      label: t("doctors.appointmentDetails.vitalRep.fields.temperature", "Temperature"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.temperature", "Enter temperature"),
    },
    {
      key: "oxygenSaturation",
      label: t("doctors.appointmentDetails.vitalRep.fields.oxygenSaturation", "Oxygen saturation"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.oxygenSaturation", "Enter oxygen saturation"),
    },
    {
      key: "respiratoryRate",
      label: t("doctors.appointmentDetails.vitalRep.fields.respiratoryRate", "Respiratory rate"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.respiratoryRate", "Enter respiratory rate"),
    },
    {
      key: "weight",
      label: t("doctors.appointmentDetails.vitalRep.fields.weight", "Weight"),
      placeholder: t("doctors.appointmentDetails.vitalRep.placeholders.weight", "Enter weight"),
    },
  ];
  const vitalRepNoteSections = [
    {
      key: "visitSummary",
      title: t("doctors.appointmentDetails.vitalRep.sections.visitSummary", "Visit summary"),
      fields: [
        {
          key: "reasonForVisit",
          label: t("doctors.appointmentDetails.vitalRep.fields.reasonForVisit", "Reason for visit"),
          rows: 2,
        },
      ],
    },
    {
      key: "subjective",
      title: t("doctors.appointmentDetails.vitalRep.sections.subjective", "Subjective"),
      fields: [
        {
          key: "clinicalObservations",
          label: t("doctors.appointmentDetails.vitalRep.fields.clinicalObservations", "Clinical observations"),
          rows: 3,
        },
      ],
    },
    {
      key: "objective",
      title: t("doctors.appointmentDetails.vitalRep.sections.objective", "Objective"),
      fields: [
        {
          key: "testsLabsRequested",
          label: t("doctors.appointmentDetails.vitalRep.fields.testsLabsRequested", "Tests & labs requested"),
          rows: 2,
        },
      ],
    },
    {
      key: "assessment",
      title: t("doctors.appointmentDetails.vitalRep.sections.assessment", "Assessment"),
      fields: [
        {
          key: "assessment",
          label: t("doctors.appointmentDetails.vitalRep.fields.assessment", "Diagnosis and assessment"),
          rows: 2,
        },
      ],
    },
    {
      key: "plan",
      title: t("doctors.appointmentDetails.vitalRep.sections.plan", "Plan"),
      fields: [
        {
          key: "medicationSummary",
          label: t("doctors.appointmentDetails.vitalRep.fields.medicationSummary", "Prescription and medication summary"),
          rows: 2,
        },
        {
          key: "treatmentNextSteps",
          label: t("doctors.appointmentDetails.vitalRep.fields.treatmentNextSteps", "Treatment and next steps"),
          rows: 3,
        },
      ],
    },
    {
      key: "followUp",
      title: t("doctors.appointmentDetails.vitalRep.sections.followUp", "Follow-up"),
      fields: [
        {
          key: "followUpAdvice",
          label: t("doctors.appointmentDetails.vitalRep.fields.followUpAdvice", "Follow-up advice"),
          rows: 2,
        },
      ],
    },
  ];
  const doctorFulfillmentSummary = buildDoctorFulfillmentSummary(prescriptionMeta, {
    deliveryMode: t("doctors.appointmentDetails.fulfillment.deliveryMode", "Delivery"),
    pickupMode: t("doctors.appointmentDetails.fulfillment.pickupMode", "Pickup"),
    selectedPharmacy: t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy"),
    readyForPickup: t("doctors.appointmentDetails.fulfillment.readyForPickup", "Ready for pickup"),
    readyForPickupDescription: t(
      "doctors.appointmentDetails.fulfillment.readyForPickupDescription",
      "{pharmacy} marked this prescription as ready for pickup.",
      { pharmacy: firstMeaningfulText([
        prescriptionMeta?.pharmacy_branch_name,
        prescriptionMeta?.pharmacy_name,
        prescriptionMeta?.requested_pharmacy_branch_name,
        prescriptionMeta?.requested_pharmacy_name,
      ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")) }
    ),
    fulfillmentModeComplete: t(
      "doctors.appointmentDetails.fulfillment.fulfillmentModeComplete",
      "{mode} handoff is complete on the pharmacy side.",
      {
        mode: formatFulfillmentModeLabel(
          prescriptionMeta?.fulfillment_mode || prescriptionMeta?.requested_fulfillment_mode || "pickup",
          {
            delivery: t("doctors.appointmentDetails.fulfillment.deliveryMode", "Delivery"),
            pickup: t("doctors.appointmentDetails.fulfillment.pickupMode", "Pickup"),
          }
        ),
      }
    ),
    paid: t("doctors.appointmentDetails.fulfillment.paid", "Paid"),
    paidDescription: t(
      "doctors.appointmentDetails.fulfillment.paidDescription",
      "{pharmacy} has a confirmed payment for this prescription.",
      { pharmacy: firstMeaningfulText([
        prescriptionMeta?.pharmacy_branch_name,
        prescriptionMeta?.pharmacy_name,
        prescriptionMeta?.requested_pharmacy_branch_name,
        prescriptionMeta?.requested_pharmacy_name,
      ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")) }
    ),
    waitForPickup: t(
      "doctors.appointmentDetails.fulfillment.waitForPickup",
      "Wait for the pharmacy to mark the order ready for pickup."
    ),
    paymentReady: t("doctors.appointmentDetails.fulfillment.paymentReady", "Payment ready"),
    paymentReadyDescription: t(
      "doctors.appointmentDetails.fulfillment.paymentReadyDescription",
      "{pharmacy} set the quote and the patient can now pay in AfriMed.",
      { pharmacy: firstMeaningfulText([
        prescriptionMeta?.pharmacy_branch_name,
        prescriptionMeta?.pharmacy_name,
        prescriptionMeta?.requested_pharmacy_branch_name,
        prescriptionMeta?.requested_pharmacy_name,
      ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")) }
    ),
    waitingForPayment: t(
      "doctors.appointmentDetails.fulfillment.waitingForPayment",
      "{mode} handoff is waiting for patient payment.",
      {
        mode: formatFulfillmentModeLabel(
          prescriptionMeta?.fulfillment_mode || prescriptionMeta?.requested_fulfillment_mode || "pickup",
          {
            delivery: t("doctors.appointmentDetails.fulfillment.deliveryMode", "Delivery"),
            pickup: t("doctors.appointmentDetails.fulfillment.pickupMode", "Pickup"),
          }
        ),
      }
    ),
    pharmacyPreparing: t("doctors.appointmentDetails.fulfillment.pharmacyPreparing", "Pharmacy preparing"),
    pharmacyPreparingDescription: t(
      "doctors.appointmentDetails.fulfillment.pharmacyPreparingDescription",
      "{pharmacy} accepted the prescription and is preparing it.",
      { pharmacy: firstMeaningfulText([
        prescriptionMeta?.pharmacy_branch_name,
        prescriptionMeta?.pharmacy_name,
        prescriptionMeta?.requested_pharmacy_branch_name,
        prescriptionMeta?.requested_pharmacy_name,
      ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")) }
    ),
    patientHandledExternally: t(
      "doctors.appointmentDetails.fulfillment.patientHandledExternally",
      "{mode} coordination is handled by the patient, Fola, and pharmacy outside the clinician workflow.",
      {
        mode: formatFulfillmentModeLabel(
          prescriptionMeta?.fulfillment_mode || prescriptionMeta?.requested_fulfillment_mode || "pickup",
          {
            delivery: t("doctors.appointmentDetails.fulfillment.deliveryMode", "Delivery"),
            pickup: t("doctors.appointmentDetails.fulfillment.pickupMode", "Pickup"),
          }
        ),
      }
    ),
    needsNewPharmacy: t("doctors.appointmentDetails.fulfillment.needsNewPharmacy", "Needs new pharmacy"),
    needsNewPharmacyDescription: t(
      "doctors.appointmentDetails.fulfillment.needsNewPharmacyDescription",
      "{pharmacy} could not fulfil this prescription.",
      { pharmacy: firstMeaningfulText([
        prescriptionMeta?.pharmacy_branch_name,
        prescriptionMeta?.pharmacy_name,
        prescriptionMeta?.requested_pharmacy_branch_name,
        prescriptionMeta?.requested_pharmacy_name,
      ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")) }
    ),
    chooseAnotherPharmacy: t(
      "doctors.appointmentDetails.fulfillment.chooseAnotherPharmacy",
      "The patient needs to choose another signed-up pharmacy."
    ),
    pharmacySelected: t("doctors.appointmentDetails.fulfillment.pharmacySelected", "Pharmacy selected"),
    pharmacySelectedDescription: t(
      "doctors.appointmentDetails.fulfillment.pharmacySelectedDescription",
      "{pharmacy} is the active {mode} handoff branch for this prescription.",
      {
        pharmacy: firstMeaningfulText([
          prescriptionMeta?.pharmacy_branch_name,
          prescriptionMeta?.pharmacy_name,
          prescriptionMeta?.requested_pharmacy_branch_name,
          prescriptionMeta?.requested_pharmacy_name,
        ], t("doctors.appointmentDetails.fulfillment.selectedPharmacy", "The selected pharmacy")),
        mode: formatFulfillmentModeLabel(
          prescriptionMeta?.fulfillment_mode || prescriptionMeta?.requested_fulfillment_mode || "pickup",
          {
            delivery: t("doctors.appointmentDetails.fulfillment.deliveryMode", "Delivery"),
            pickup: t("doctors.appointmentDetails.fulfillment.pickupMode", "Pickup"),
          }
        ).toLowerCase(),
      }
    ),
    issued: t("doctors.appointmentDetails.fulfillment.issued", "Issued"),
    issuedDescription: t(
      "doctors.appointmentDetails.fulfillment.issuedDescription",
      "Prescription was issued. The patient starts pharmacy coordination after opening it in AfriMed™."
    ),
  });
  const vitalRepClinicalDisplaySections = buildVitalRepClinicalDisplaySections({
    vitalRepNote,
    structuredData: vitalRepStructuredData,
    sourceSnapshot: vitalRepSourceSnapshot,
    prescriptionMeta,
    prescriptionLabel: hasIssuedPrescription ? prescriptionLabel : "",
    fulfillmentSummary: hasIssuedPrescription ? doctorFulfillmentSummary : null,
    labels: {
      visitReason: t("doctors.appointmentDetails.vitalRep.display.visitReason", "Subjective"),
      objective: t("doctors.appointmentDetails.vitalRep.display.objective", "Objective"),
      clinicalImpression: t(
        "doctors.appointmentDetails.vitalRep.display.clinicalImpression",
        "Assessment"
      ),
      treatmentPlan: t("doctors.appointmentDetails.vitalRep.display.treatmentPlan", "Plan"),
      notRecorded: t("doctors.appointmentDetails.vitalRep.display.notRecorded", "Not recorded."),
    },
  });
  const vitalRepAuditTimelineItems = buildVitalRepAuditTimeline(vitalRepNote);

  async function persistAppointmentPatch(patch, actionKey = "") {
    if (!appointmentId) return restored || null;

    setActionBusy(actionKey);
    setActionError("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      let query = supabase
        .from("appointments")
        .update({
          ...patch,
          updated_at: new Date().toISOString(),
        })
        .eq("id", appointmentId);

      if (user?.id) {
        query = query.eq("doctor_id", user.id);
      }

      const { data, error } = await query
        .select("id,status,notes,appointment_date,duration_minutes,consultation_type")
        .maybeSingle();

      if (error) throw error;

      const nextSnapshot = {
        ...(restored || {}),
        ...(patch || {}),
        ...(data || {}),
      };

      setLiveSnapshot(nextSnapshot);
      persistDraft(appointmentId, nextSnapshot);
      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      return nextSnapshot;
    } catch (error) {
      setActionError(
        error?.message ||
          t(
            "doctors.appointmentDetails.errors.update",
            "Could not update appointment right now."
          )
      );
      return restored || null;
    } finally {
      setActionBusy("");
    }
  }

  function openLabAttachmentPicker() {
    window.dispatchEvent(new CustomEvent('afrimed:clinical-draft', {detail:{appointmentId,kind:'lab_request'}}));
    document.querySelector('[data-testid="clinical-actions"]')?.scrollIntoView({behavior:'smooth',block:'start'});
  }

  function handleLabAttachmentsChange(event) {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;

    const existing = Array.isArray(restored?.attachments) ? restored.attachments : [];
    const additions = files.map((file, index) => ({
      id: `doctor-attachment-${Date.now()}-${index}`,
      fileName: String(file?.name || "").trim() || t("doctors.appointmentDetails.attachments.untitled", "Attachment"),
      name: String(file?.name || "").trim() || t("doctors.appointmentDetails.attachments.untitled", "Attachment"),
      source: t("doctors.appointmentDetails.attachments.byDoctor", "By doctor"),
      type: String(file?.type || "").toLowerCase(),
      created_at: new Date().toISOString(),
      origin: "attachment",
    }));

    const nextSnapshot = {
      ...(restored || {}),
      attachments: [...existing, ...additions],
    };

    setLiveSnapshot(nextSnapshot);
    persistDraft(appointmentId, nextSnapshot);
    setToastMsg(
      files.length === 1
        ? t("doctors.appointmentDetails.attachments.addedSingle", "Attachment added.")
        : t("doctors.appointmentDetails.attachments.addedMultiple", "{count} attachments added.", { count: files.length })
    );

    if (event?.target) {
      event.target.value = "";
    }
  }

  function removeLabAttachment(itemId) {
    const current = Array.isArray(restored?.attachments) ? restored.attachments : [];
    const nextAttachments = current.filter((item) => String(item?.id || "") !== String(itemId || ""));
    if (nextAttachments.length === current.length) return;

    const nextSnapshot = {
      ...(restored || {}),
      attachments: nextAttachments,
    };

    setLiveSnapshot(nextSnapshot);
    persistDraft(appointmentId, nextSnapshot);
    setToastMsg(t("doctors.appointmentDetails.attachments.removed", "Attachment removed."));
  }

  function buildConsultationState(snapshot) {
    const doctorIdentity = resolveDoctorIdentity(snapshot || restored);
    return {
      appointmentId,
      actorRole: "doctor",
      counterpartName: patientName,
      counterpartAvatar: avatar,
      patientId,
      patient: patient || null,
      restored: snapshot || restored || null,
      patientName,
      doctorName: doctorIdentity.name,
      doctor: {
        name: doctorIdentity.name,
        full_name: doctorIdentity.name,
        photo_url: doctorIdentity.photo,
      },
      meetUrl: meetUrl || "",
    };
  }

  async function openAudioCallSession() {
    // Joining media owns the server-authorised session transition. Merely
    // opening the audio screen must not start a non-video clinical encounter.
    nav(`/appointments/call/${appointmentId}`, {
      state: { ...buildConsultationState(restored), mediaKind: "audio" },
    });
  }

  function handleMessageClick() {
    if (isPastInteractionDisabled) return;

    if (!patientId) {
      setToastMsg("The patient identity is unavailable. Refresh the appointment before messaging.");
      return;
    }
    nav(`/messages/${patientId}`, {
      state: {
        conversationTitle: patientName,
        conversationType: "patient",
        appointmentId: String(appointmentId),
        patientId,
        participantUserId: patientId,
        returnTo: location.pathname,
      },
    });
  }

  function handleSmsPreviewClick() {
    if (isPastInteractionDisabled) return;
    setAccessLayerSmsOpen((current) => !current);
  }

  function handleCallClick() {
    if (isPastInteractionDisabled) return;

    if (!canOpenLiveCall) {
      setToastMsg(
        t(
          "doctors.appointmentDetails.toast.callOnlyDuringAppointment",
          "Only available for appointments"
        )
      );
      return;
    }

    openAudioCallSession({
      skipProgressUpdate: isReadOnlyDetailsVariant,
    });
  }

  async function openConsultation() {
    if (isPastInteractionDisabled) return;

    if (visitType !== "evisit") {
      await openAudioCallSession();
      return;
    }

    setActionBusy("consultation");
    setActionError("");

    try {
      await primeConsultationMediaPermission({ appointmentId });
    } catch (permissionError) {
      logVideoConsultationStartDebug({
        appointmentId: String(appointmentId || ""),
        appointmentStatus: status || "",
        action: "prime_media_permission",
        errorName: permissionError?.name || "",
        errorMessage: permissionError?.message || String(permissionError || ""),
      });
      setToastMsg(
        permissionError?.message ||
          t(
            "doctors.appointmentDetails.toast.cameraPermissionRequired",
            "Allow camera and microphone access to start the consultation."
          )
      );
      setActionBusy("");
      return;
    }

    const needsProgressStatus =
      status !== "in_progress" &&
      status !== "completed" &&
      status !== "missed" &&
      status !== "cancelled";

    const snapshot = needsProgressStatus
      ? await persistAppointmentPatch({ status: "in_progress" }, "consultation")
      : restored;

    try {
      const { data: startedSession, error: sessionStartError } =
        await videoConsultationService.markAppointmentSessionStarted(
          appointmentId,
          {
            connectionState: "doctor_started",
            deviceState: {
              camera: "permission_primed",
              microphone: "permission_primed",
            },
          }
        );

      if (sessionStartError) throw sessionStartError;

      logVideoConsultationStartDebug({
        appointmentId: String(appointmentId || ""),
        appointmentStatus: snapshot?.status || status || "",
        hasVideoSession: Boolean(startedSession?.session?.id),
        videoSessionStatus: startedSession?.session?.status || "",
        action: "doctor_start_session",
      });
    } catch (sessionStartError) {
      logVideoConsultationStartDebug({
        appointmentId: String(appointmentId || ""),
        appointmentStatus: snapshot?.status || status || "",
        action: "doctor_start_session",
        errorName: sessionStartError?.name || "",
        errorMessage: sessionStartError?.message || String(sessionStartError || ""),
      });
      setToastMsg(
        t(
          "doctors.appointmentDetails.toast.consultationStartFailed",
          "The video consultation could not be started right now. Please try again."
        )
      );
      setActionBusy("");
      return;
    }

    nav(`/appointments/video/${appointmentId}`, {
      state: {
        ...buildConsultationState(snapshot),
        mediaPermissionPrimed: true,
      },
    });
  }

  async function completeConsultation() {
    if (consultationAlreadyCompleted) {
      setCompleteDialogOpen(false);
      return;
    }

    if (!canCompleteConsultation) {
      setActionError(completeConsultationRequirementsLabel);
      setCompleteDialogOpen(false);
      return;
    }

    setActionBusy("complete");
    setActionError("");

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      const doctorId = String(user?.id || "").trim();
      if (authError || !doctorId) {
        throw new Error(t("doctors.appointmentDetails.errors.auth", "Authentication required."));
      }

      const hadGrantedEhrAccess = ehrIsGranted;

      if (hadGrantedEhrAccess && appointmentId && patientId) {
        await revokeAppointmentEhrAccess({
          appointmentId,
          doctorId,
          doctorRef: doctorId,
          patientUserId: patientId,
          patientRef:
            restored?.patient_row_id ||
            restored?.patient_id ||
            restored?.patientId ||
            restored?.patient?.patient_id ||
            restored?.patient?.id ||
            patient?.patient_id ||
            patient?.id ||
            null,
        });
      }

      const { data: completion, error: completionError } = await supabase.rpc("afrimed_complete_clinician_consultation", { p_appointment_id: appointmentId });
      if (completionError) throw completionError;
      const snapshot = { ...(restored || {}), ...completion, status: "completed" };
      // Clinical completion remains durable even if financial reconciliation is pending.
      await appointmentReservationCall("process", appointmentId);
      const doctorIdentity = resolveDoctorIdentity(snapshot || restored);
      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      setCompleteDialogOpen(false);

      nav(`/appointments/summary/${appointmentId}`, {
        state: {
          actorRole: "doctor",
          appointmentId,
          patientId,
          patient: patient || null,
          restored: {
            ...(snapshot || restored || {}),
            ehrAccessStatus: hadGrantedEhrAccess
              ? "revoked"
              : ehrStatusFromServer(ehrReq),
            ehrRevokedAt: hadGrantedEhrAccess ? new Date().toISOString() : null,
            consultationEnded: true,
          },
          counterpartName: patientName,
          counterpartAvatar: avatar,
          doctorName: doctorIdentity.name,
          prescriptionId:
            snapshot?.prescriptionId ||
            snapshot?.prescription?.id ||
            restored?.prescriptionId ||
            restored?.prescription?.id ||
            null,
          endedAt: new Date().toISOString(),
        },
      });
    } catch (error) {
      setActionError(
        error?.message ||
          t(
            "doctors.appointmentDetails.errors.completeConsultation",
            "Could not complete consultation right now."
          )
      );
    } finally {
      setActionBusy("");
    }
  }

  function openPrescription() {
    if (hasIssuedPrescription) {
      return nav(`/appointments/prescriptions/${prescriptionId}`, {
        state: {
          from: "doctor-appointment-details",
          actorRole: "doctor",
          appointmentId,
          prescriptionId,
          patientId,
          patient: patient || null,
          restored: {
            ...(restored || {}),
            prescriptionId,
          },
          returnTo: `/doctors/appointments/${String(appointmentId)}`,
          returnState: {
            actorRole: "doctor",
            appointmentId,
            patientId,
            patient: patient || null,
            restored: {
              ...(restored || {}),
              prescriptionId,
            },
            prescriptionId,
          },
        },
      });
    }

    nav(`/appointments/prescriptions/new/${appointmentId}`, {
      state: {
        actorRole: "doctor",
        appointmentId,
        patientId,
        patient: patient || null,
        restored: restored || null,
        returnTo: `/doctors/appointments/${String(appointmentId)}`,
        returnState: {
          actorRole: "doctor",
          appointmentId,
          patientId,
          patient: patient || null,
          restored: restored || null,
        },
      },
    });
  }

  function openDiagnosis() {
    nav(`/doctors/appointments/${appointmentId}/diagnosis`, {
      state: restored || null,
    });
  }

  function openVitalRepBeta(mode = "") {
    const query = mode ? `?mode=${encodeURIComponent(mode)}` : "";
    nav(`/doctors/appointments/${appointmentId}/vitalrep-beta${query}`, {
      replace: true,
      state: {
        appointmentId,
        appointment,
        patient: patient || patientProfilePayload || null,
        patientUserId,
        doctorUserId: doctorUserId || authDoctorUserId,
        amendmentReason: vitalRepAmendmentDraft?.amendmentReason || vitalRepAmendmentReason || "",
        returnTo: `/doctors/appointments/${String(appointmentId)}`,
        returnState: restored || null,
      },
    });
  }

  function openVitalRepAmendmentDialog() {
    setVitalRepAmendmentReason(vitalRepAmendmentDraft?.amendmentReason || "");
    setVitalRepError("");
    setVitalRepAmendmentDialogOpen(true);
  }

  function continueVitalRepAmendment() {
    const nextReason = String(vitalRepAmendmentReason || "").trim();
    if (!nextReason) {
      setVitalRepError(
        t(
          "doctors.appointmentDetails.vitalRep.errors.amendmentReason",
          "Add an amendment reason before continuing."
        )
      );
      return;
    }
    const draft = {
      amendmentReason: nextReason,
      startedAt: new Date().toISOString(),
    };
    writeVitalRepAmendmentDraft(appointmentId, draft);
    setVitalRepAmendmentDraft(draft);
    setVitalRepAmendmentDialogOpen(false);
    setVitalRepError("");
    openVitalRepBeta("amend");
  }

  async function openVitalRepEditor() {
    setVitalRepError("");
    if (!canShowVitalRepAction || vitalRepIsLoading) return;
    if (vitalRepHasFinal) {
      openVitalRepAmendmentDialog();
      return;
    }
    openVitalRepBeta("");
  }

  async function requestEhrAccess() {
    try {
      setActionError("");
      const apptId = String(appointmentId || "");
      if (!apptId) throw new Error("Missing appointmentId");

      const nextPatientId = resolvePatientUserId(
        restored?.patient_user_id,
        restored?.patient?.user_id,
        restored?.patientProfile?.user_id,
        patient?.user_id,
        restored?.patientId,
        restored?.patient_id,
        restored?.patient?.id,
        restored?.patientProfile?.id,
        patient?.id,
      );
      const canonicalPatientId = await resolvePatientAuthUserId({
        appointmentId: apptId,
        patientUserId: nextPatientId,
        patientRef:
          restored?.patient_row_id ||
          restored?.patient?.patient_id ||
          restored?.patientProfile?.patient_id ||
          restored?.patient_id ||
          null,
      });

      const { data: authData, error: authErr } = await supabase.auth.getUser();
      const doctorId = authData?.user?.id || null;
      const requestResult = await submitConfirmedEhrRequest({
        appointmentId: apptId,
        doctorId: authErr ? null : doctorId,
        patientId: canonicalPatientId,
        submit: () => requestAppointmentEhrAccess({
              appointmentId: apptId,
              doctorId,
              patientUserId: canonicalPatientId,
              patientRef:
                restored?.patient_row_id ||
                restored?.patient?.patient_id ||
                restored?.patientProfile?.patient_id ||
                restored?.patient_id ||
                null,
              doctorName:
                restored?.doctor?.name ||
                restored?.doctor?.full_name ||
                doctorFallbackLabel,
            }),
      });

      const nextState = {
        ...(restored || {}),
        patientId: canonicalPatientId,
        patient_user_id: canonicalPatientId,
        ehrAccessStatus: "requested",
        ehrRequestId: requestResult.requestId,
        ehrRequestedAt: requestResult?.requestedAt || new Date().toISOString(),
      };

      setLiveSnapshot(nextState);
      persistDraft(apptId, nextState);

      nav(`/doctors/appointments/${apptId}/ehr-request`, {
        state: nextState,
        replace: true,
      });
    } catch (error) {
      console.warn("EHR request failed:", error);
      setActionError(
        error?.message ||
          t(
            "doctors.appointmentDetails.errors.requestEhr",
            "Could not request EHR access right now."
          )
      );
    }
  }

  function openEhrDetails() {
    const routePatientId =
      patientId ||
      patientProfilePayload?.user_id ||
      patientProfilePayload?.id ||
      null;

    if (!routePatientId || !ehrIsGranted) {
      return nav(`/doctors/appointments/${appointmentId}/ehr-request`, {
        state: { appointmentId, patient: patient || null, restored: restored || null },
      });
    }

    nav(`/doctors/patients/${routePatientId}/ehr?appointmentId=${encodeURIComponent(String(appointmentId))}`, {
      state: {
        appointmentId,
        patient: patientProfilePayload,
        restored: {
          ...(restored || {}),
          ehrAccessStatus: "granted",
          patientId: routePatientId,
        },
        patientId: routePatientId,
      },
    });
  }

  function openPastConsultations() {
    const routePatientId =
      patientId ||
      patientProfilePayload?.user_id ||
      patientProfilePayload?.id ||
      null;

    if (!routePatientId || !ehrIsGranted) return;

    nav(`/doctors/patients/${encodeURIComponent(String(routePatientId))}/ehr/past-appointments`, {
      state: {
        from: "doctor-appointment-details",
        appointmentId,
        patientId: routePatientId,
        patient: patientProfilePayload || patient || null,
        restored: {
          ...(restored || {}),
          ehrAccessStatus: "granted",
          patientId: routePatientId,
        },
        returnTo: `/doctors/appointments/${String(appointmentId)}`,
        returnState: restored || null,
      },
    });
  }

  function openLabsResults() {
    if (typeof document === "undefined") return;
    const target = document.getElementById("appointment-tests-labs");
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setToastMsg(
      t(
        "doctors.appointmentDetails.patientRecord.labsUnavailable",
        "Tests and lab results are available from the consultation documentation area."
      )
    );
  }

  function openReferralHandover() {
    nav(`/doctors/appointments/${appointmentId}/refer`, {
      state: {
        appointmentId,
        patientUserId,
        doctorUserId,
        appointment,
        patient: patient || patientProfilePayload || null,
        returnTo: `/doctors/appointments/${String(appointmentId)}`,
        returnState: restored || null,
      },
    });
  }

  async function copyAccessLayerSmsValue(value, label) {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        throw new Error("clipboard_unavailable");
      }
      await navigator.clipboard.writeText(text);
      setToastMsg(t("doctors.appointmentDetails.accessLayerSms.copied", "{label} copied.", { label }));
    } catch {
      setToastMsg(
        t(
          "doctors.appointmentDetails.accessLayerSms.copyFailed",
          "Could not copy right now."
        )
      );
    }
  }

  function refreshAccessLayerSmsPreview() {
    if (accessLayerSmsRefreshBusy) return;
    setAccessLayerSmsRefreshBusy(true);
    setAccessLayerPreviewVersion((current) => current + 1);
    window.setTimeout(() => {
      setAccessLayerSmsRefreshBusy(false);
    }, 450);
  }

  async function sendAccessLayerTestSms() {
    const testRecipientPhone = normalizeSmsProofPhone(accessLayerTestRecipientPhone);
    if (!testRecipientPhone || !/^\+[1-9]\d{7,14}$/.test(testRecipientPhone)) {
      setAccessLayerSmsSendResult({
        ok: false,
        status: "failed",
        errorMessage: t(
          "doctors.appointmentDetails.accessLayerSms.invalidTestPhone",
          "Enter an approved test phone number in international format, for example +263..."
        ),
      });
      return;
    }

    setAccessLayerSmsSendBusy(true);
    setAccessLayerSmsSendResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("access-layer-sms-test", {
        body: {
          appointment_id: appointmentId,
          test_recipient_phone: testRecipientPhone,
          confirm_test_only: true,
        },
      });
      if (error) throw error;
      setAccessLayerSmsSendResult(data || { ok: false, status: "failed" });
      if (data?.ok) {
        setAccessLayerPreviewVersion((current) => current + 1);
        setToastMsg(
          t(
            "doctors.appointmentDetails.accessLayerSms.testSent",
            "Staging test SMS sent and dispatch evidence persisted."
          )
        );
      }
    } catch (error) {
      setAccessLayerSmsSendResult({
        ok: false,
        status: "failed",
        errorMessage:
          error?.message ||
          t("doctors.appointmentDetails.accessLayerSms.sendFailed", "SMS proof send failed."),
      });
    } finally {
      setAccessLayerSmsSendBusy(false);
    }
  }

  const accessLayerSmsDispatchMetadata =
    accessLayerSmsDispatchEvidence?.metadata_json &&
    typeof accessLayerSmsDispatchEvidence.metadata_json === "object"
      ? accessLayerSmsDispatchEvidence.metadata_json
      : {};
  const accessLayerSmsDispatchSentAt = formatAccessLayerDispatchTimestamp(
    accessLayerSmsDispatchEvidence?.sent_at || accessLayerSmsDispatchEvidence?.created_at
  );
  const accessLayerSmsDispatchSensitiveDataExcluded =
    accessLayerSmsDispatchMetadata.sensitive_data_excluded !== false;

  const accessLayerSmsPreviewCard = accessLayerSmsPreviewVisible && accessLayerSmsOpen ? (
    <Card className="mt-5 overflow-hidden">
      <div className="px-4 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="text-title-medium text-on-surface">
              {t("doctors.appointmentDetails.accessLayerSms.title", "Access Layer SMS preview")}
            </div>
            <div className="mt-1 text-body-medium text-on-surface-var">
              {accessLayerSmsPreview?.missingPhone
                ? t(
                    "doctors.appointmentDetails.accessLayerSms.missingPhoneStatus",
                    "Cannot send SMS preview to missing phone"
                  )
                : t(
                    "doctors.appointmentDetails.accessLayerSms.status",
                    "Preview only — SMS provider not enabled"
                  )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="tonal"
              size="sm"
              onClick={() =>
                copyAccessLayerSmsValue(
                  accessLayerSmsPreview?.message,
                  t("doctors.appointmentDetails.accessLayerSms.message", "Message")
                )
              }
            >
              {t("doctors.appointmentDetails.accessLayerSms.copyMessage", "Copy message")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                copyAccessLayerSmsValue(
                  accessLayerSmsPreview?.referenceCode,
                  t("doctors.appointmentDetails.accessLayerSms.reference", "Reference")
                )
              }
            >
              {t("doctors.appointmentDetails.accessLayerSms.copyReference", "Copy reference")}
            </Button>
            <Button
              type="button"
              variant="text"
              size="sm"
              className="gap-2"
              disabled={accessLayerSmsRefreshBusy}
              onClick={refreshAccessLayerSmsPreview}
            >
              <RefreshCw className={`h-4 w-4 ${accessLayerSmsRefreshBusy ? "animate-spin" : ""}`} aria-hidden />
              {accessLayerSmsRefreshBusy
                ? t("doctors.appointmentDetails.accessLayerSms.refreshing", "Refreshing...")
                : t("doctors.appointmentDetails.accessLayerSms.refresh", "Refresh preview")}
            </Button>
          </div>
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 grid gap-3 md:grid-cols-2">
        <div>
          <div className="text-body-small text-on-surface-var">
            {t("doctors.appointmentDetails.accessLayerSms.appointmentReference", "Appointment reference")}
          </div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerSmsPreview?.referenceCode || "AFM-PREVIEW"}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">
            {t("doctors.appointmentDetails.accessLayerSms.recipientPhone", "Recipient phone")}
          </div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerSmsPreview?.recipientPhone ||
              t("doctors.appointmentDetails.accessLayerSms.notAvailable", "Not available")}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">
            {t("doctors.appointmentDetails.accessLayerSms.appointmentTime", "Appointment date/time")}
          </div>
          <div className="mt-1 text-body-large text-on-surface">
            {dateLabel !== dashLabel && startTimeLabel !== dashLabel
              ? `${dateLabel}, ${startTimeLabel}`
              : t("doctors.appointmentDetails.accessLayerSms.scheduledTime", "Scheduled time")}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">
            {t("doctors.appointmentDetails.accessLayerSms.providerState", "Provider state")}
          </div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerProviderState}
          </div>
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3">
        <div className="text-body-small text-on-surface-var">
          {t("doctors.appointmentDetails.accessLayerSms.messagePreview", "Message preview")}
        </div>
        <div className="mt-2 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.04)] px-3 py-3 text-body-medium leading-6 text-on-surface">
          {accessLayerSmsPreview?.message}
        </div>
        <div className="mt-3 text-body-small text-on-surface-var">
          {t("doctors.appointmentDetails.accessLayerSms.sensitiveData", "Sensitive data excluded")}:{" "}
          <span className="text-on-surface">
            {accessLayerSmsPreview?.sensitiveDataExcluded
              ? t("common.yes", "Yes")
              : t("common.no", "No")}
          </span>
        </div>
      </div>

      {accessLayerSmsProofSendVisible ? (
        <>
          <Divider inset variant="outlineVariant" />
          <div className="px-4 py-3">
            <div className="text-title-small text-on-surface">
              {t("doctors.appointmentDetails.accessLayerSms.providerProof", "Staging provider proof")}
            </div>
            <div className="mt-1 text-body-small text-on-surface-var">
              {t(
                "doctors.appointmentDetails.accessLayerSms.providerProofHelp",
                "Sends only to an approved test number. This does not use the patient phone."
              )}
            </div>
            {accessLayerSmsDispatchEvidenceStatus === "loading" ? (
              <div className="mt-3 text-body-small text-on-surface-var">
                {t(
                  "doctors.appointmentDetails.accessLayerSms.evidenceLoading",
                  "Loading dispatch evidence..."
                )}
              </div>
            ) : null}
            {accessLayerSmsDispatchEvidence ? (
              <div className="mt-3">
                <div className="text-title-small text-on-surface">
                  {t(
                    "doctors.appointmentDetails.accessLayerSms.dispatchEvidence",
                    "Controlled SMS dispatch evidence"
                  )}
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.dispatchStatus", "Dispatch status")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.status || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.provider", "Provider")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {formatAccessLayerProvider(accessLayerSmsDispatchEvidence.provider)}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t(
                        "doctors.appointmentDetails.accessLayerSms.twilioMessageSid",
                        "Twilio Message SID"
                      )}
                    </div>
                    <div className="mt-1 break-all text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.provider_message_id || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.dispatchId", "Dispatch ID")}
                    </div>
                    <div className="mt-1 break-all text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.id || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.sentRecipient", "Recipient")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.recipient_phone || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.sentAt", "Sent at")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchSentAt || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.appointmentReference", "Appointment reference")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.reference_code || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">
                      {t("doctors.appointmentDetails.accessLayerSms.testFlag", "Test flag")}
                    </div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.is_test
                        ? t("common.yes", "Yes")
                        : t("common.no", "No")}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-body-small text-on-surface-var">
                  {t("doctors.appointmentDetails.accessLayerSms.messagePreview", "Message preview")}
                </div>
                <div className="mt-2 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.04)] px-3 py-3 text-body-medium leading-6 text-on-surface">
                  {accessLayerSmsDispatchEvidence.message_body || accessLayerSmsPreview?.message}
                </div>
                <div className="mt-3 text-body-small text-on-surface-var">
                  {t("doctors.appointmentDetails.accessLayerSms.sensitiveData", "Sensitive data excluded")}:{" "}
                  <span className="text-on-surface">
                    {accessLayerSmsDispatchSensitiveDataExcluded
                      ? t("common.yes", "Yes")
                      : t("common.no", "No")}
                  </span>
                </div>
              </div>
            ) : accessLayerSmsDispatchEvidenceStatus === "loaded" ? (
              <div className="mt-3 text-body-small text-on-surface-var">
                {t(
                  "doctors.appointmentDetails.accessLayerSms.noDispatchEvidence",
                  "No controlled SMS dispatch evidence is stored for this appointment yet."
                )}
              </div>
            ) : null}
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <div>
                <TextField56
                  id="access-layer-test-recipient-phone"
                  type="tel"
                  label={t(
                    "doctors.appointmentDetails.accessLayerSms.testRecipient",
                    "Approved test recipient"
                  )}
                  tone="surface"
                  value={accessLayerTestRecipientPhone}
                  onChange={(value) => {
                    setAccessLayerTestRecipientPhone(value);
                    setAccessLayerSmsSendResult(null);
                  }}
                  placeholder="+41796246335"
                  inputMode="tel"
                  autoComplete="tel"
                />
                <div className="mt-2 text-body-small text-on-surface-var">
                  {t(
                    "doctors.appointmentDetails.accessLayerSms.testRecipientHelp",
                    "Required for staging proof; patient phone is not used for real SMS."
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="tonal"
                size="sm"
                className="gap-2"
                disabled={accessLayerSmsSendBusy || !accessLayerTestRecipientPhone.trim()}
                onClick={sendAccessLayerTestSms}
              >
                <TintedIcon
                  src={ICONS.sendSms}
                  className="h-4 w-4"
                  color="rgb(var(--afrimed-primary))"
                />
                {accessLayerSmsSendBusy
                  ? t("doctors.appointmentDetails.accessLayerSms.sending", "Sending...")
                  : t("doctors.appointmentDetails.accessLayerSms.sendTest", "Send test SMS")}
              </Button>
            </div>

            {accessLayerSmsSendResult ? (
              <div
                className={`mt-3 rounded-[12px] px-3 py-3 text-body-small ${
                  accessLayerSmsSendResult.ok
                    ? "bg-[rgba(22,163,74,0.08)] text-[#15803d]"
                    : "bg-[rgba(180,35,24,0.08)] text-[#b42318]"
                }`}
              >
                <div>
                  {t("doctors.appointmentDetails.accessLayerSms.sendStatus", "Status")}:{" "}
                  <span className="font-semibold">
                    {accessLayerSmsSendResult.status || "failed"}
                  </span>
                </div>
                {accessLayerSmsSendResult.provider ? (
                  <div>
                    {t("doctors.appointmentDetails.accessLayerSms.provider", "Provider")}:{" "}
                    {accessLayerSmsSendResult.provider}
                  </div>
                ) : null}
                {accessLayerSmsSendResult.dispatchId ? (
                  <div>
                    {t("doctors.appointmentDetails.accessLayerSms.dispatchId", "Dispatch ID")}:{" "}
                    {accessLayerSmsSendResult.dispatchId}
                  </div>
                ) : null}
                {accessLayerSmsSendResult.providerMessageIdMasked ? (
                  <div>
                    {t(
                      "doctors.appointmentDetails.accessLayerSms.providerMessageId",
                      "Provider message ID"
                    )}:{" "}
                    {accessLayerSmsSendResult.providerMessageIdMasked}
                  </div>
                ) : null}
                {accessLayerSmsSendResult.recipientPhoneMasked ? (
                  <div>
                    {t("doctors.appointmentDetails.accessLayerSms.sentRecipient", "Recipient")}:{" "}
                    {accessLayerSmsSendResult.recipientPhoneMasked}
                  </div>
                ) : null}
                {accessLayerSmsSendResult.sentAt ? (
                  <div>
                    {t("doctors.appointmentDetails.accessLayerSms.sentAt", "Sent at")}:{" "}
                    {accessLayerSmsSendResult.sentAt}
                  </div>
                ) : null}
                {accessLayerSmsSendResult.error || accessLayerSmsSendResult.errorMessage ? (
                  <div>
                    {t("doctors.appointmentDetails.accessLayerSms.error", "Error")}:{" "}
                    {accessLayerSmsSendResult.errorMessage || accessLayerSmsSendResult.error}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Card>
  ) : null;

  const lifecycleAction =
    isPastInteractionDisabled || isStaticConfirmedDetails
      ? null
      : status === "confirmed"
        ? {
            key: "progress",
            label: visitType === "evisit" ? startConsultationLabel : markInProgressLabel,
            onClick: () => persistAppointmentPatch({ status: "in_progress" }, "progress"),
          }
        : canCompleteConsultation
          ? {
              key: "complete",
              label: completeConsultationLabel,
              onClick: () => setCompleteDialogOpen(true),
          }
        : null;

  if (detailPlaceholderState === "loading") {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title={pageTitle}
            onBack={handleBack}
            backIconSrc="/icons/doctor/appointments/arrow_back.svg"
          />

          <Card className="mt-6 p-4 text-body-medium text-on-surface-var">
            {loadingBody}
          </Card>
        </div>
      </div>
    );
  }

  if (detailPlaceholderState === "not-found") {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
        <div className="mx-auto w-full max-w-[720px] lg:max-w-[1008px] px-4 pt-5 pb-24">
          <div className="relative flex items-center justify-center">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t("common.back", "Back")}
              className="absolute left-0 top-1/2 -translate-y-1/2"
            >
              <img src={ICONS.back} alt="" className="h-6 w-6" draggable="false" />
            </button>

            <div className="text-title-large text-on-surface">{pageTitle}</div>
          </div>

          <Card className="mt-6 p-4 text-body-medium text-on-surface-var">
            {notFoundBody}
            <div className="mt-3">
              <Button fullWidth onClick={() => nav("/doctors/appointments")}>
                {backToAppointmentsLabel}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div
        className={`afrimed-rail afrimed-rail--comfortable pt-4 ${
          isTimedEvisitDetails ? "pb-36" : "pb-24"
        }`}
      >
        <TopBarCenter
          title={pageTitle}
          subtitle={isCompleteConsultationEvisitDetails ? `${startTimeLabel} - ${endTimeLabel}` : ""}
          onBack={handleBack}
        />

        {toastMsg ? (
          <div
            className="
              fixed left-1/2 -translate-x-1/2
              bottom-[92px] md:bottom-6
              w-full max-w-[720px] px-4
              z-[80]
            "
          >
            <div
              className="
                w-full
                rounded-[12px]
                bg-[#0B0F1A]
                text-white
                px-4 py-3
                flex items-center justify-between gap-3
                shadow-[0_10px_30px_rgba(2,6,23,0.35)]
              "
              role="status"
              aria-live="polite"
            >
              <div className="text-body-medium truncate">{toastMsg}</div>

              <button
                type="button"
                onClick={() => setToastMsg("")}
                aria-label={t("common.close", "Close")}
                className="
                  shrink-0
                  h-9 w-9
                  rounded-[10px]
                  bg-[rgba(255,255,255,0.08)]
                  hover:bg-[rgba(255,255,255,0.12)]
                  active:bg-[rgba(255,255,255,0.12)]
                  flex items-center justify-center
                "
              >
                <span className="text-white text-[18px] leading-none translate-y-[-1px]">×</span>
              </button>
            </div>
          </div>
        ) : null}

      {/* Patient row + Profile */}
        <div className="mt-7 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <AvatarThumb68
              src={resolvedPatientAvatar}
              alt={resolvedPatientName}
              role="patient"
              shape="circle"
              style={{ width: 44, height: 44 }}
            />

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-body-large text-on-surface truncate">{resolvedPatientName}</div>
                <img src={patientVerificationIcon} alt="" className="h-4 w-4 shrink-0" draggable="false" />
              </div>
              <div className="text-body-medium text-on-surface-var truncate">
                {patientMeta || dashLabel}
              </div>
              {careSubjectType === "dependant" && guardianName ? (
                <div className="text-body-small text-on-surface-var truncate">
                  {t("doctors.appointmentDetails.responsibleAdult", "Responsible adult")}:{" "}
                  <span className="text-on-surface">
                    {guardianName}
                    {responsibleRelationship ? ` • ${responsibleRelationship}` : ""}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              nav(`/doctors/appointments/${appointmentId}/patient-profile`, {
                state: {
                  from: "doctor",
                  patientId:
                    restored?.patient_row_id ||
                    restored?.patient?.patient_id ||
                    restored?.patientProfile?.patient_id ||
                    patientProfilePayload?.patient_id ||
                    restored?.patientId ||
                    null,
                  patientUserId: patientId || patientProfilePayload?.user_id || null,
                  patient: patientProfilePayload,
                  appointmentId,
                  ehrGranted: ehrIsGranted,
                  restored: restored
                    ? {
                        ...restored,
                        doctor: restored?.doctor
                          ? {
                              ...restored.doctor,
                              id:
                                restored.doctor.id ||
                                restored.doctor.doctor_id ||
                                restored.doctor.profile_id ||
                                restored.doctor.doctor_user_id ||
                                restored.doctor.user_id ||
                                restored.doctorId ||
                                null,
                              doctorId:
                                restored.doctor.doctorId ||
                                restored.doctor.doctor_id ||
                                restored.doctor.id ||
                                restored.doctor.profile_id ||
                                restored.doctor.doctor_user_id ||
                                restored.doctor.user_id ||
                                restored.doctorId ||
                                null,
                              doctor_id:
                                restored.doctor.doctor_id ||
                                restored.doctor.doctorId ||
                                restored.doctor.id ||
                                restored.doctor.profile_id ||
                                restored.doctor.doctor_user_id ||
                                restored.doctor.user_id ||
                                restored.doctorId ||
                                null,
                              user_id:
                                restored.doctor.user_id ||
                                restored.doctor.doctor_user_id ||
                                restored.doctor.id ||
                                restored.doctorId ||
                                null,
                              doctor_user_id:
                                restored.doctor.doctor_user_id ||
                                restored.doctor.user_id ||
                                restored.doctor.id ||
                                restored.doctorId ||
                                null,
                              profile_id:
                                restored.doctor.profile_id ||
                                restored.doctor.id ||
                                null,
                              favoriteIds: routeDoctorFavoriteIds,
                              isFavorite: restored.doctor.isFavorite === true,
                            }
                          : restored?.doctor,
                      }
                    : null,
                },
              })
            }
            className="flex items-center gap-1 text-body-medium text-on-surface-var"
          >
            <span>{profileLabel}</span>
            <ChevronRight className="h-5 w-5 text-on-surface-var" aria-hidden />
          </button>
        </div>

        {isCompleteConsultationEvisitDetails ? (
          <>
            <div className="mt-3">
              <Divider variant="outlineVariant" />
            </div>

            <div className="flex items-center gap-3 pr-4 py-4">
              <img src={ICONS.consultType} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
              <div className="min-w-0 flex-1 text-body-large text-on-surface truncate">{apptTypeLabel}</div>
              <div className="shrink-0 text-body-large text-on-surface">
                {consultationPriceLabel}
              </div>
            </div>

            <Divider variant="outlineVariant" />

            {actionError && !consultationIncomplete ? (
              <div className="mt-4 rounded-[12px] bg-[rgba(180,35,24,0.08)] px-4 py-3 text-body-medium text-[#b42318]">
                {actionError}
              </div>
            ) : null}

            {ehrAccessSurface}

	            <div className="mt-5">
	              <div className="mb-3 flex items-center justify-between gap-3">
	                <div className="min-w-0">
	                  <div className="text-body-medium text-on-surface-var">{diagnosisLabel}</div>
	                </div>
                {hasDiagnosisContent ? (
                  <button
                    type="button"
                    onClick={openVitalRepEditor}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]"
                    aria-label={editDiagnosisLabel}
                  >
                    <img
                      src={ICONS.editSquare}
                      alt=""
                      className="h-6 w-6 opacity-90"
                      draggable="false"
                    />
                  </button>
                ) : null}
              </div>
              {hasDiagnosisContent ? (
                <div className="grid grid-cols-1 gap-3">
                  {(vitalRepNote?.authorityReportId ? [{key:"approved-assessment", label: t("clinical.noteApproved"), value: vitalRepNote.soapDraft?.assessment || vitalRepNote.structuredClinicalData?.assessment || ""}] : []).map((section) => (
                    <ClinicalDisplayCard key={section.key} section={section} />
                  ))}
                </div>
              ) : (
                <CompactAddRow
                  icon={ICONS.diagnosis}
                  label={t("clinical.notePending")}
                  actionLabel={addLabel}
                  onClick={openVitalRepEditor}
                />
              )}
            </div>

            <ClinicalNoteProvenance note={vitalRepNote}/>
            <div className="mt-4">
              {canShowVitalRepAction ? (
                <div className="pt-2">
                  <details
                    className="group"
                    ref={vitalRepPreviewDetailsRef}
                    open={vitalRepPreviewOpen}
                    onToggle={(event) => setVitalRepPreviewOpen(event.currentTarget.open)}
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 marker:hidden">
                      <div className="min-w-0 text-title-small text-on-surface-var">
                        {vitalRepSectionLabel}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {vitalRepIsLoading ? (
                          <div className={neutralChipClass}>
                            {vitalRepLoadingLabel}
                          </div>
                        ) : vitalRepStatusLabel ? (
                          <div className={confirmedChipClass}>
                            {vitalRepStatusLabel}
                          </div>
                        ) : null}
                        <div className="text-label-medium text-[rgb(var(--afrimed-primary))]">
                          <span className="group-open:hidden">
                            {t("common.expand", "Expand")}
                          </span>
                          <span className="hidden group-open:inline">
                            {t("common.collapse", "Collapse")}
                          </span>
                        </div>
                      </div>
                    </summary>

                    <div className="mt-3 flex flex-col gap-3">
                      {vitalRepIsLoading ? (
                        <div className="text-body-medium text-on-surface-var">
                          {vitalRepLoadingLabel}
                        </div>
                      ) : !vitalRepStatusLabel ? (
                        <div className="text-body-medium text-on-surface">
                          {vitalRepHelperLabel}
                        </div>
                      ) : null}

                      {vitalRepIsLoading ? null : vitalRepHasFinal && vitalRepPreviewText ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {vitalRepClinicalDisplaySections.slice(0, 4).map((section) => (
                            <ClinicalDisplayCard key={section.key} section={section} />
                          ))}
                        </div>
                      ) : vitalRepHasFinal ? (
                        <div className="text-body-medium leading-6 text-on-surface-var">
                          {vitalRepEditorFinalHelperLabel}
                        </div>
                      ) : vitalRepPreviewText ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {vitalRepClinicalDisplaySections.map((section) => (
                            <ClinicalDisplayCard key={section.key} section={section} />
                          ))}
                        </div>
                      ) : (
                        <div className="text-body-medium leading-6 text-on-surface-var">
                          {vitalRepPreviewFallbackLabel}
                        </div>
                      )}

                      {!vitalRepIsLoading && vitalRepHasFinal ? (
                        <VitalRepAuditTimeline items={vitalRepAuditTimelineItems} />
                      ) : null}

                      {!vitalRepIsLoading && !vitalRepHasFinal ? (
                        <div className="rounded-[12px] bg-[rgba(var(--afrimed-primary),0.04)] px-4 py-3">
                          <div className="text-title-small text-on-surface">
                            {t(
                              "doctors.appointmentDetails.vitalRep.transcriptDraft.title",
                              "VitalRep™ transcript draft beta"
                            )}
                          </div>
                          <div className="mt-1 text-body-medium leading-5 text-on-surface-var">
                            {t(
                              "doctors.appointmentDetails.vitalRep.transcriptDraft.body",
                              "Capture or paste consultation notes, then generate a SOAP Draft for doctor review before final sign-off."
                            )}
                          </div>
                        </div>
                      ) : null}

                    </div>
                  </details>

	                  <div className="mt-4">
	                    <Button
	                      type="button"
	                      variant="tonal"
	                      size="lg"
	                      className="min-h-[44px] w-full justify-center rounded-[12px] px-5 text-label-large text-[rgb(var(--afrimed-primary))]"
	                      onClick={openVitalRepEditor}
	                      disabled={Boolean(vitalRepBusy) || vitalRepIsLoading}
	                    >
	                      {vitalRepIsLoading
	                        ? vitalRepLoadingLabel
	                        : vitalRepBusy === "generate"
	                          ? updatingLabel
	                          : vitalRepCtaLabel}
	                    </Button>
	                  </div>

                  {vitalRepError ? (
                    <div className="mt-2 text-body-small text-[rgb(var(--clr-error))]">
                      {vitalRepError}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <ClinicalActionPanel appointmentId={appointmentId} />

            {canShowReferralActions ? referralHandoverRow : null}

            <div className="mt-4">
              <CompactAddRow
                icon={ICONS.medication}
                label={prescriptionSectionLabel}
                actionLabel={hasIssuedPrescription ? viewLabel : addLabel}
                onClick={openPrescription}
                iconStyle={appointmentActionIconStyle}
              />
            </div>

            {hasIssuedPrescription ? (
              <div className="mt-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-title-small text-on-surface-var">
                      {t("doctors.appointmentDetails.fulfillment.title", "Prescription fulfilment")}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface">
                      {doctorFulfillmentSummary.description}
                    </div>
                    {doctorFulfillmentSummary.helper ? (
                      <div className="mt-2 text-body-small leading-5 text-on-surface-var">
                        {doctorFulfillmentSummary.helper}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={[
                      doctorFulfillmentSummary.toneClass.includes("#008A8F")
                        ? confirmedChipClass
                        : neutralChipClass,
                      doctorFulfillmentSummary.toneClass,
                    ].join(" ")}
                  >
                    {doctorFulfillmentSummary.label}
                  </div>
                </div>
              </div>
            ) : null}

            <div id="appointment-tests-labs" className="mt-4 scroll-mt-24">
              <CompactAddRow
                icon={ICONS.biotech}
                label={testsLabResultsLabel}
                actionLabel={addLabel}
                onClick={openLabAttachmentPicker}
                iconStyle={appointmentActionIconStyle}
              />
            </div>

            {hasDocumentContent ? (
              <div className="mt-5">
                <div className="mb-3 text-body-medium text-on-surface-var">{testsDocumentsLabel}</div>
                <Card className="overflow-hidden">
                  {labItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <img src={item.icon} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
                        <div className="min-w-0 flex-1">
                          <div className="text-body-large text-on-surface truncate">{item.title}</div>
                          <div className="text-body-medium text-on-surface-var truncate">{item.subtitle}</div>
                        </div>
                        {item.origin === "attachment" ? (
                          <button
                            type="button"
                            onClick={() => removeLabAttachment(item.id)}
                            className="shrink-0 rounded-full p-1 transition hover:bg-[rgba(2,6,23,0.06)]"
                            aria-label={t("doctors.appointmentDetails.attachments.remove", "Remove attachment")}
                          >
                            <TintedIcon
                              src={ICONS.circleX}
                              color={removeAttachmentIconColor}
                              className="h-5 w-5"
                            />
                          </button>
                        ) : null}
                      </div>
                      {index < labItems.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
                    </React.Fragment>
                  ))}
                </Card>
              </div>
            ) : null}

            <input
              id={labAttachmentInputId}
              ref={attachmentInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.csv,.txt"
              multiple
              className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
              onChange={handleLabAttachmentsChange}
            />

            {/* Patient Photos */}
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <div className="text-body-medium text-on-surface-var">{patientPhotosLabel}</div>
                <button
                  type="button"
                  onClick={() => nav(`/doctors/appointments/${appointmentId}/patientphotos`, { state: restoredWithPhotos })}
                  className="text-body-medium text-[rgb(var(--afrimed-primary))]"
                >
                  {showAllLabel}
                </button>
              </div>

            {photoPreview.length > 0 ? (
              <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pr-4">
                  {photoPreview.map((img, index) => (
                    <div
                      key={String(img.id || img.url)}
                      className={[
                        "overflow-hidden bg-[rgba(2,6,23,0.04)] shrink-0",
                        index === 0 ? "h-[158px] w-[300px] rounded-[28px]" : "h-[158px] w-[56px] rounded-[28px]",
                      ].join(" ")}
                    >
                      <img
                        src={img.url}
                        alt={t("doctors.appointmentDetails.photosAlt", "Patient photo")}
                        className="h-full w-full object-cover"
                        draggable="false"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Card className="mt-3 p-4 text-body-medium text-on-surface-var">
                  {photosEmptyLabel}
                </Card>
              )}
            </div>

            <div className="mt-5">
              {!consultationAlreadyCompleted && !canCompleteConsultation ? (
                <div className="mb-3 text-body-small text-on-surface-var">
                  {completeConsultationRequirementsLabel}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  if (!canCompleteConsultation || consultationAlreadyCompleted) return;
                  setCompleteDialogOpen(true);
                }}
                disabled={actionBusy === "complete" || !canCompleteConsultation || consultationAlreadyCompleted}
                className={[
                  "flex h-12 w-full items-center justify-center gap-2 rounded-[12px] disabled:cursor-not-allowed disabled:opacity-60",
                  isDark
                    ? "bg-[rgba(29,79,215,0.18)] text-[rgb(var(--afrimed-primary))]"
                    : "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]",
                ].join(" ")}
              >
                <img
                  src={ICONS.completeSmall}
                  alt=""
                  className="hidden"
                  draggable="false"
                />
                <TintedIcon
                  src={ICONS.completeSmall}
                  color={
                    isDark
                      ? "rgb(var(--afrimed-primary))"
                      : "rgb(var(--afrimed-on-primary))"
                  }
                  className="h-[18px] w-[18px]"
                />
                <span className="text-label-large">
                  {consultationAlreadyCompleted
                    ? completionLockedLabel
                    : actionBusy === "complete"
                      ? updatingLabel
                      : completeConsultationLabel}
                </span>
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Appointment summary card */}
            <Card className="mt-5 overflow-hidden">
              {/* time + status */}
              <div className="px-4 py-3 flex items-center gap-3">
                <img src={ICONS.schedule} alt="" className="h-6 w-6 opacity-80" draggable="false" />

                <div className="min-w-0 flex-1">
                  <div className="text-body-large text-on-surface">
                    {startTimeLabel} - {endTimeLabel}
                  </div>
                  <div className="text-body-medium text-on-surface-var truncate">
                    {visitType === "evisit" || isTimedEvisitDetails ? compactDateLabel : dateLabel}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-body-medium text-on-surface-var">
                <span>{statusPresentation.label}</span>
                <img src={statusPresentation.icon} alt="" className="h-5 w-5" draggable="false" />
                </div>
              </div>

              <Divider inset variant="outlineVariant" />

              {/* appointment type */}
              <div className="px-4 py-3 flex items-center gap-3">
                <img src={ICONS.first} alt="" className="h-6 w-6 opacity-80" draggable="false" />
                <div className="min-w-0 flex-1">
                  <div className="text-body-large text-on-surface truncate">{apptTypeLabel}</div>
                </div>
                <div className="text-body-large text-on-surface-var">
                {consultationPriceLabel}
                </div>
              </div>

              <Divider inset variant="outlineVariant" />

              {/* location / evisit */}
              {visitType === "evisit" ? (
                <div className="px-4 py-3 flex items-center gap-3">
                  <img
                    src={ICONS.evisit}
                    alt=""
                    className="h-6 w-6 shrink-0 opacity-80"
                    draggable="false"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-body-large text-on-surface">{eVisitLabel}</div>
                    <div className="text-body-medium text-on-surface-var truncate">{meetUrlLabel}</div>
                  </div>

                  {canOpenLiveCall ? (
                    <button
                      type="button"
                      onClick={openConsultation}
                      className="
                        h-10 px-4 rounded-[12px]
                        bg-[rgb(var(--afrimed-primary))]
                        text-[rgb(var(--afrimed-on-primary))]
                        text-label-large
                        flex items-center gap-2
                      "
                    >
                      {joinLabel}
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="px-4 py-3 flex items-center gap-3">
                  <img src={ICONS.hospital} alt="" className="h-6 w-6 opacity-80" draggable="false" />
                  <div className="min-w-0 flex-1">
                    <div className="text-body-large text-on-surface truncate">{facilityName}</div>
                  </div>
                </div>
              )}
            </Card>

            {/* Message / SMS / Call actions */}
            <div
              className={`mt-5 grid gap-4 ${
                accessLayerSmsPreviewVisible ? "grid-cols-3" : "grid-cols-2"
              }`}
            >
              <Button
                type="button"
                variant="ghost"
                disabled={isPastInteractionDisabled}
                className={`h-12 gap-2 rounded-[12px] px-2 text-label-large text-[rgb(var(--afrimed-primary))] ${ghostActionDisabledClass}`}
                onClick={handleMessageClick}
              >
                <TintedIcon
                  src={ICONS.messageBubble}
                  className="h-[18px] w-[18px]"
                  color="rgb(var(--afrimed-primary))"
                />
                {messageLabel}
              </Button>

              {accessLayerSmsPreviewVisible ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isPastInteractionDisabled}
                  className={`h-12 gap-2 rounded-[12px] px-2 text-label-large ${ghostActionDisabledClass}`}
                  onClick={handleSmsPreviewClick}
                  aria-expanded={accessLayerSmsOpen}
                >
                  <TintedIcon
                    src={ICONS.sms}
                    className="h-[18px] w-[18px]"
                    color="rgb(var(--afrimed-primary))"
                  />
                  {smsLabel}
                </Button>
              ) : null}

              <Button
                type="button"
                variant="ghost"
                disabled={isPastInteractionDisabled || !canOpenLiveCall}
                title="Internet audio call using your microphone. No telephone number is dialled."
                className={`h-12 gap-2 rounded-[12px] px-2 text-label-large text-[rgb(var(--afrimed-primary))] ${ghostActionDisabledClass}`}
                onClick={handleCallClick}
              >
                <TintedIcon
                  src={ICONS.call}
                  className="h-[18px] w-[18px]"
                  color="rgb(var(--afrimed-primary))"
                />
                {callLabel}
              </Button>
            </div>

            {accessLayerSmsPreviewCard}

            {actionError ? (
              <div className="mt-4 rounded-[12px] bg-[rgba(180,35,24,0.08)] px-4 py-3 text-body-medium text-[#b42318]">
                {actionError}
              </div>
            ) : null}

            {!isReadOnlyDetailsVariant && !isCompleteConsultationEvisitDetails && lifecycleAction ? (
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                {lifecycleAction ? (
                  <button
                    type="button"
                    onClick={lifecycleAction.onClick}
                    disabled={!!actionBusy}
                    className="
                      h-12 rounded-[12px] bg-[rgb(var(--afrimed-primary))]
                      px-4 text-label-large text-[rgb(var(--afrimed-on-primary))]
                      disabled:cursor-not-allowed disabled:opacity-60
                    "
                  >
                    {actionBusy === lifecycleAction.key ? updatingLabel : lifecycleAction.label}
                  </button>
                ) : null}
              </div>
            ) : null}

            {ehrAccessSurface}

            {["in_progress", "completed"].includes(status) ? <ClinicalActionPanel appointmentId={appointmentId} /> : null}

            {canShowReferralActions ? referralHandoverRow : null}

            {/* Patient Photos */}
            <div className="mt-7">
              <div className="flex items-center justify-between">
                <div className="text-title-medium text-on-surface-var">{patientPhotosLabel}</div>
                <button
                  type="button"
                  onClick={() => nav(`/doctors/appointments/${appointmentId}/patientphotos`, { state: restoredWithPhotos })}
                  className="text-body-medium text-[rgb(var(--afrimed-primary))]"
                >
                  {showAllLabel}
                </button>
              </div>

              {photoPreview.length > 0 ? (
                <div className="mt-3 flex gap-4 overflow-x-auto no-scrollbar pr-4">
                  {photoPreview.map((img) => (
                    <div
                      key={String(img.id || img.url)}
                      className="w-[380px] h-[205px] rounded-[28px] overflow-hidden bg-[rgba(2,6,23,0.04)] shrink-0"
                    >
                      <img
                        src={img.url}
                        alt={t("doctors.appointmentDetails.photosAlt", "Patient photo")}
                        className="w-full h-full object-cover"
                        draggable="false"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Card className="mt-3 p-4 text-body-medium text-on-surface-var">
                  {photosEmptyLabel}
                </Card>
              )}
            </div>

            {canShowReferralActions ? (
              <div className="mt-6 space-y-3">
                <div>
                  <div className="text-title-medium text-on-surface">
                    {t("referrals.entry.postConsultActions", "Post-consult actions")}
                  </div>
                  <div className="text-body-medium text-on-surface-var">
                    {t(
                      "referrals.entry.postConsultActionsSubtitle",
                      "Continue the patient’s care journey."
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    nav(`/doctors/appointments/${appointment.id}/refer`, {
                      state: {
                        appointmentId: appointment.id,
                        patientUserId,
                        doctorUserId,
                      },
                    })
                  }
                  className="
                    w-full h-12 rounded-[12px]
                    border border-transparent
                    text-label-large text-on-surface
                    bg-[rgb(var(--clr-surface-cont-highest))]
                  "
                >
                  {t("referrals.doctor.title", "Refer patient")}
                </button>
              </div>
            ) : null}

            {/* Change appointment list item (blueprint order) */}
            {/* Support */}
            <div className="mt-6">
            <div className="text-title-medium text-on-surface-var mb-3">{supportLabel}</div>

            <Card className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    if (isPastInteractionDisabled) return;
                    setChangeSheetOpen(true);
                  }}
                  disabled={isPastInteractionDisabled}
                  className="w-full px-4 py-4 flex items-center gap-3 text-left"
                >
                  <TintedIcon
                    src={ICONS.change}
                    className="h-6 w-6"
                    opacity={isPastInteractionDisabled ? 0.7 : 0.95}
                    color={
                      isPastInteractionDisabled
                        ? "rgb(var(--clr-on-surface-variant))"
                        : "rgb(var(--clr-on-surface))"
                    }
                  />
                  <div className="flex-1">
                    <div
                      className={`text-body-large ${isPastInteractionDisabled ? "text-on-surface-var" : "text-on-surface"}`}
                    >
                      {changeAppointmentLabel}
                    </div>
                    {isPastInteractionDisabled ? (
                      <div className="text-body-medium text-on-surface-var">
                        {unavailablePastAppointmentsLabel}
                      </div>
                    ) : null}
                  </div>
                  <TintedIcon
                    src="/icons/list-items/chevron-right.svg"
                    className={`h-6 w-6 ${isPastInteractionDisabled ? "opacity-65" : "opacity-90"}`}
                    color="rgb(var(--clr-on-surface-variant))"
                  />
                </button>
            </Card>
            </div>
          </>
        )}

        <AppointmentCancelDialog
          open={cancelOpen}
          title={cancelDialogTitle}
          description={cancelDialogDescription}
          cancelText={t("common.no", "No")}
          confirmText={cancelConfirmLabel}
          textAlign="left"
          maxWidthClassName="max-w-[312px]"
          onCancel={() => setCancelOpen(false)}
          onConfirm={async () => {
            setCancelOpen(false);
            const nextSnapshot = {
              ...(restored || {}),
              status: "cancelled",
            };
            let cancelled = false;
            try {
              const { error } = await supabase
                .from("appointments")
                .update({
                  status: "cancelled",
                  status_reason: "cancelled",
                  updated_at: new Date().toISOString(),
                })
                .eq("id", appointmentId);
              if (error) throw error;
              setLiveSnapshot(nextSnapshot);
              persistDraft(appointmentId, nextSnapshot);
              window.dispatchEvent(new Event("afrimed:appointments:changed"));
              cancelled = true;
            } catch (error) {
              console.warn("Doctor cancel appointment failed:", error);
            }
            if (!cancelled) return;
            nav("/doctors/appointments", {
              replace: true,
              state: {
                justCancelled: true,
                cancelledAppointmentId: appointmentId,
                cancelledSnapshot: nextSnapshot,
              },
            });
          }}
        />

        <AppointmentDeleteDialog
          open={deleteOpen}
          title={deleteDialogTitle}
          description={deleteDialogDescription}
          cancelText={t("common.no", "No")}
          confirmText={deleteConfirmLabel}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            setDeleteOpen(false);

            try {
              const { error } = await supabase
                .from("appointments")
                .delete()
                .eq("id", appointmentId);
              if (error) throw error;
              sessionStorage.removeItem(`afrimed:doctor_appt:${String(appointmentId)}`);
              window.dispatchEvent(new Event("afrimed:appointments:changed"));
            } catch {}

            nav("/doctors/appointments", { replace: true });
          }}
        />

        <BasicDialogCritical
          open={completeDialogOpen}
          title={completeConsultationLabel}
          iconSrc="/icons/check-ok.svg"
          body={completeConsultationDialogBody}
          cancelText={t("common.cancel", "Cancel")}
          okText={
            actionBusy === "complete"
              ? updatingLabel
              : t("doctors.appointmentDetails.completeConsultationDialog.confirmShort", "Complete")
          }
          onCancel={() => {
            if (actionBusy === "complete") return;
            setCompleteDialogOpen(false);
          }}
          onOk={completeConsultation}
          disableBackdropClose={actionBusy === "complete"}
        />

        {vitalRepAmendmentDialogOpen ? (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6"
          >
            <div
              className="absolute inset-0 bg-black/35"
              onClick={() => {
                setVitalRepAmendmentDialogOpen(false);
                setVitalRepAmendmentReason("");
                setVitalRepError("");
              }}
            />
            <BasicDialogFrame
              role="dialog"
              aria-modal="true"
              titleId="vitalrep-amendment-dialog-title"
              title={vitalRepSectionLabel}
              body={
                <div className="text-left">
                  <div className="text-body-medium leading-6 text-on-surface-var">
                    {t(
                      "doctors.appointmentDetails.vitalRep.amendmentIntro",
                      "Add a reason before continuing to VitalRep™ beta. The amendment will be saved with audit metadata."
                    )}
                  </div>

                  <label className="mt-5 block">
                    <span className="text-title-small text-on-surface">
                      {t("doctors.appointmentDetails.vitalRep.amendmentReason", "Amendment reason")}
                    </span>
                    <textarea
                      value={vitalRepAmendmentReason}
                      onChange={(event) => setVitalRepAmendmentReason(event.target.value)}
                      rows={4}
                      className="mt-2 w-full resize-y rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.7)] bg-[rgb(var(--clr-surface-container-low))] px-3 py-3 text-body-medium leading-6 text-on-surface outline-none transition focus:border-[rgba(var(--afrimed-primary),0.32)]"
                      placeholder={t(
                        "doctors.appointmentDetails.vitalRep.amendmentReasonPlaceholder",
                        "Explain why this finalised note needs an amendment."
                      )}
                    />
                  </label>

                  {vitalRepError ? (
                    <div className="mt-3 text-body-small text-[rgb(var(--clr-error))]">
                      {vitalRepError}
                    </div>
                  ) : null}
                </div>
              }
              contentClassName="pt-7 pb-3 sm:pt-8 sm:pb-4"
              actionsClassName="pt-2 sm:pt-3"
            >
              <button
                type="button"
                className={BASIC_DIALOG_ACTION_CLASS}
                style={{
                  background: "rgba(29, 79, 215, 0.16)",
                  color: "rgb(var(--clr-on-surface))",
                  borderColor: "rgba(29, 79, 215, 0.40)",
                  boxShadow: "0 3px 6px -3px rgba(29, 79, 215, 0.35)",
                }}
                onClick={() => {
                  setVitalRepAmendmentDialogOpen(false);
                  setVitalRepAmendmentReason("");
                  setVitalRepError("");
                }}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={BASIC_DIALOG_ACTION_CLASS}
                style={{
                  background: "rgb(var(--afrimed-primary))",
                  color: "rgb(var(--afrimed-on-primary))",
                  borderColor: "rgb(var(--afrimed-primary))",
                }}
                onClick={continueVitalRepAmendment}
              >
                {t("doctors.appointmentDetails.vitalRep.continueToBeta", "Continue to VitalRep™ beta")}
              </button>
            </BasicDialogFrame>
          </div>
        ) : null}

        <Sheet open={changeSheetOpen} onClose={() => setChangeSheetOpen(false)}>
          <div className="pb-2">
            <div className="text-title-large text-on-surface">{changeSheetTitle}</div>
          </div>

          <div className="mt-3 -mx-6">
            <button
              type="button"
              onClick={() => {
                if (isPastInteractionDisabled) return;
                setChangeSheetOpen(false);
                nav(`/appointments/details/${appointmentId}/edit`, { state: editAppointmentState });
              }}
              className={[
                "flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left",
                isPastInteractionDisabled ? "cursor-not-allowed opacity-50" : "",
              ].join(" ")}
            >
              <img src={ICONS.scheduleFill} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
              <div className="min-w-0 flex-1 text-body-large text-on-surface">{requestNewTimeLabel}</div>
              <img src={ICONS.next} alt="" className="h-6 w-6 shrink-0 opacity-70" draggable="false" />
            </button>

            <Divider inset variant="outlineVariant" />

            <button
              type="button"
              onClick={() => {
                if (isPastInteractionDisabled) return;
                setChangeSheetOpen(false);
                setCancelOpen(true);
              }}
              className={[
                "flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left",
                isPastInteractionDisabled ? "cursor-not-allowed opacity-50" : "",
              ].join(" ")}
            >
              <img src={ICONS.cancelAction} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
              <div className="min-w-0 flex-1 text-body-large text-on-surface">{cancelAppointmentLabel}</div>
              <img src={ICONS.next} alt="" className="h-6 w-6 shrink-0 opacity-70" draggable="false" />
            </button>

            <Divider inset variant="outlineVariant" />
          </div>
        </Sheet>
      </div>

      {isTimedEvisitDetails && (canOpenLiveCall || isStartsSoonEvisitDetails || isStartConsultationEvisitDetails) ? (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface))] px-4 pt-3 pb-9">
          <div className="mx-auto w-full max-w-[380px]">
            {canOpenLiveCall ? (
              <button
                type="button"
                onClick={openConsultation}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-[12px] bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]"
              >
                <TintedIcon
                  src={ICONS.stethoscope}
                  className="h-[18px] w-[18px]"
                  color="rgb(var(--afrimed-on-primary))"
                />
                <span className="text-label-large">{startConsultationLabel}</span>
              </button>
            ) : (
              <div className="flex h-12 items-center justify-center gap-2 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.08)] text-on-surface-var">
                <img
                  src={ICONS.stethoscope}
                  className="h-[18px] w-[18px]"
                  alt=""
                  draggable="false"
                  style={mutedSupportIconStyle}
                />
                <span className="text-label-large">{startsInLabel}</span>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
